import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Container,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Fade,
  IconButton,
  Popover,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
  Autocomplete,
  TextField,
  Checkbox,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  RadioGroup,
  Radio,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Storage as DataIcon,
  CloudQueue as CloudIcon,
  Computer as MockIcon,
  Download as DownloadIcon,
  FilterAlt as FilterIcon,
  Info as InfoIcon,
  UnfoldMore as ExpandAllIcon,
  UnfoldLess as CollapseAllIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  AnalyticsConfig,
  AnalyticsTab,
  AnalyticsDashboardProps,
  AnalyticsDataQuery,
  AnalyticsResponse,
  FilterConfig,
  AnalyticsVisualization
} from '../types/analytics.types';

// Extended interface for tab data that includes visualizations
interface TabDataResponse extends AnalyticsResponse {
  visualizations: Record<string, any>;
}

// Filter state with metadata for conflict detection
interface FilterState {
  values: Record<string, any>;
  version: number;
  timestamp: number;
  tabId: string;
}

import { KpiComponent } from './KpiComponent';
import { useAnalyticsService } from '../hooks/useAnalytics';

/** Normalize order type for comparison: DINE_IN=DINE-IN=DINEIN, TAKE_OUT=TAKEOUT=TAKE-OUT, CALL_IN=CALLIN=CALL-IN */
const normalizeOrderType = (v: string) => String(v || '').replace(/[-_\s]/g, '').toUpperCase();

/** Get net_sales for a specific order type from a row (handles unpivoted and pivoted data) */
const getNetSalesForOrderType = (row: any, orderTypeFilter: string): number => {
  if (row.hasOwnProperty('order_type') && row.hasOwnProperty('net_sales')) {
    return normalizeOrderType(row.order_type) === normalizeOrderType(orderTypeFilter)
      ? (Number(row.net_sales) || 0) : 0;
  }
  const key = Object.keys(row || {}).find(k => normalizeOrderType(k) === normalizeOrderType(orderTypeFilter));
  return key ? (Number(row[key]) || 0) : 0;
};
import { PerformanceCard } from './PerformanceCard';
import { MixBarCard } from './MixBarCard';
import { AnalyticsChart } from './AnalyticsChart';
import { AnalyticsTable, AnalyticsTableRef } from './AnalyticsTable';
import { ExportButton } from './ExportButton';
import { UnifiedFilterComponent } from './UnifiedFilterComponent';
import { DateRangeFilter } from './DateRangeFilter';
import { NumberRangeFilter } from './NumberRangeFilter';
import { GeographicMap } from './GeographicMap';
import { AiInsightCard } from './AiInsightCard';
import { GlobalFilterPanel } from './GlobalFilterPanel';
import { PropertyEditor } from './PropertyEditor';
import { RecentFiltersMenu } from './RecentFiltersMenu';
import { SmartDataManager } from '../services/smart-data.manager';
import { convertHierarchyConfigToFilterOptions } from '../utils/hierarchical-filter-utils';
import { DateConstantsService } from '../services/date-constants.service';
import { PlaceholderAnalyticsService } from '../services/placeholder-analytics.service';
import { BackendAnalyticsService } from '../services/backend-analytics.service';
import { TimeGroupingService } from '../services/time-grouping.service';
import { useConfigValidation } from '../hooks/useConfigValidation';
import { ConfigValidationPanel } from './ConfigValidationPanel';
import { useApiConfig } from '../hooks/useApiConfig';
import { AnalyticsEndpointBuilder } from '../utils/endpoint-builder';
import { analyticsPreloader } from '../services/analytics-preloader.service';
import { useFilterHistory } from '../hooks/useFilterHistory';

// Helper function to ensure sparkline has 6-10 data points by sampling/interpolating
// Defined at module scope so it is available to KpiVisualizationRenderer
const ensureOptimalSparklinePoints = (
  dataPoints: Array<{ value: number; date: string }>,
  targetMin: number = 6,
  targetMax: number = 10
): Array<{ value: number; date: string }> => {
  if (dataPoints.length === 0) return dataPoints;
  if (dataPoints.length >= targetMin && dataPoints.length <= targetMax) return dataPoints;
  if (dataPoints.length < targetMin) return dataPoints;
  if (dataPoints.length > targetMax) {
    const sampled: Array<{ value: number; date: string }> = [];
    const step = (dataPoints.length - 1) / (targetMax - 1);
    for (let i = 0; i < targetMax; i++) {
      const index = Math.round(i * step);
      sampled.push(dataPoints[index]);
    }
    return sampled;
  }
  return dataPoints;
};

/**
 * Shown when a data-backed visualization finished loading with zero rows.
 * Avoids misleading KPI/chart/table “zeros” while still leaving loading skeletons for in-flight requests.
 */
const VisualizationEmptyState: React.FC<{
  title?: string;
  description?: string;
  vizType: string;
  tallHeight?: boolean;
}> = ({ title, description, vizType, tallHeight = false }) => {
  const isChart = vizType === 'chart';
  const isTable = vizType === 'table';
  const isPerformance = vizType === 'performance-card';
  const cardHeight = tallHeight ? '12rem' : isPerformance ? '7.625rem' : '10.5rem';
  const minH = isChart ? 280 : isTable ? 220 : cardHeight;

  return (
    <Paper
      variant="outlined"
      sx={{
        height: isChart || isTable ? '100%' : undefined,
        minHeight: minH,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        boxSizing: 'border-box',
        borderRadius: '8px',
        bgcolor: 'background.paper',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ textAlign: 'center', maxWidth: 380 }}>
        {title && (
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
            {title}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          No data available for the current filters or time range.
        </Typography>
        {description && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
            {description}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};


/**
 * Memoized sub-component to handle the computationally heavy Variance and YoY (Year-over-Year) calculations
 * for KPI visualizations. This offloads consistent logic from the main dashboard render loop.
 */
const KpiVisualizationRenderer = React.memo(({
  visualization,
  data,
  filterValues,
  commonProps,
  isVizLoading,
  tabId,
  errors,
  tallHeight,
  cardCount,
  getNetSalesForOrderType
}: any) => {
  const { config: vizConfig, title, description } = visualization;
  const primaryMetric = vizConfig?.primaryMetric || '';
  const aggregation: 'sum' | 'avg' | 'min' | 'max' | 'count' = vizConfig?.aggregation || 'sum';

  const kpiDateFilterId = vizConfig?.dateFilterId || 'global-date-range';
  const dateFilter = filterValues[kpiDateFilterId];

  const serverData = data?.visualizations?.[visualization.id]?.data;
  const visualizationData = (serverData && serverData.length > 0)
    ? serverData
    : (isVizLoading ? [] : (visualization.exampleResponse || []));

  // ─── SINGLE SCAN ─────────────────────────────────────────────────────────────
  // One pass through visualizationData partitions rows into currentRows (selected
  // date range) and previousRows (prior equivalent period). Every downstream
  // calculation — KPI value, variance, sparkline — reads from these small slices
  // instead of re-scanning the full dataset independently.
  // ─────────────────────────────────────────────────────────────────────────────
  const partitionedData = useMemo(() => {
    const empty = { currentRows: [] as any[], previousRows: [] as any[], isSingleDay: false, minDate: new Date(), maxDate: new Date() };
    if (!visualizationData.length) return empty;

    const hasTimeSeries = visualizationData.some((r: any) => r.date || r.dateFormatted || r.timeKey || r.time);
    if (!hasTimeSeries) return { ...empty, currentRows: visualizationData };

    if (!dateFilter?.start || !dateFilter?.end) return { ...empty, currentRows: visualizationData };

    // Resolve dates (inline to avoid closure overhead)
    const resolveD = (v: any): Date => {
      if (!v) return new Date();
      if (v instanceof Date) return v;
      if (typeof v === 'string') {
        if (DateConstantsService.isDateConstant(v)) return new Date(DateConstantsService.resolveDateConstant(v));
        return new Date(v);
      }
      return new Date(v);
    };

    const minDate = resolveD(dateFilter.start);
    const maxDate = resolveD(dateFilter.end);
    const currentStartStr = minDate.toISOString().split('T')[0];
    const currentEndStr = maxDate.toISOString().split('T')[0];

    const startDateOnly = new Date(currentStartStr + 'T00:00:00');
    const endDateOnly = new Date(currentEndStr + 'T00:00:00');
    const numberOfDays = Math.round((endDateOnly.getTime() - startDateOnly.getTime()) / 86400000) + 1;

    const prevEnd = new Date(startDateOnly);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - (numberOfDays - 1));
    const prevStartStr = prevStart.toISOString().split('T')[0];
    const prevEndStr = prevEnd.toISOString().split('T')[0];

    const currentRows: any[] = [];
    const previousRows: any[] = [];

    for (let i = 0; i < visualizationData.length; i++) {
      const row = visualizationData[i];
      const rawDate = row.date || row.dateFormatted || row.timeKey || row.time;
      if (!rawDate) continue;
      const dateStr = typeof rawDate === 'string'
        ? (rawDate.includes('T') ? rawDate.split('T')[0] : rawDate)
        : new Date(rawDate).toISOString().split('T')[0];

      if (dateStr >= currentStartStr && dateStr <= currentEndStr) {
        // Normalize 'date' field; spread only when needed
        currentRows.push(row.date === dateStr ? row : { ...row, date: dateStr });
      } else if (dateStr >= prevStartStr && dateStr <= prevEndStr) {
        previousRows.push(row);
      }
    }

    return { currentRows, previousRows, isSingleDay: numberOfDays === 1, minDate, maxDate };
  }, [visualizationData, dateFilter]);

  // ─── AGGREGATION (no scanning — just reduce over pre-sliced arrays) ───────────
  const kpiProps = useMemo(() => {
    const { currentRows, previousRows, isSingleDay } = partitionedData;
    const hasTimeSeries = visualizationData.length > 0 &&
      visualizationData.some((r: any) => r.date || r.dateFormatted || r.timeKey || r.time);

    let kpiValue = 0;
    if (hasTimeSeries && primaryMetric) {
      const rows = currentRows;
      const orderTypeFilter = vizConfig?.orderTypeFilter;
      const isOrderTypeMixMode = vizConfig?.mode === 'orderTypeMix' && orderTypeFilter;
      const isFormulaMode = vizConfig?.mode === 'formula';
      const formula = vizConfig?.formula;

      if (isOrderTypeMixMode) {
        const salesForType = rows.reduce((s: number, r: any) => s + getNetSalesForOrderType(r, orderTypeFilter), 0);
        const salesTotal = rows.reduce((s: number, r: any) => {
          if (r.hasOwnProperty('net_sales')) return s + (Number(r.net_sales) || 0);
          return s + Object.entries(r || {}).reduce((acc: number, [k, v]) => {
            if (['date', 'dateFormatted', 'timeKey', 'time', 'id', 'recordCount'].includes(k)) return acc;
            const n = Number(v); return acc + (isNaN(n) ? 0 : n);
          }, 0);
        }, 0);
        kpiValue = salesTotal > 0 ? (salesForType / salesTotal) * 100 : 0;
      } else if (isFormulaMode && formula && typeof formula === 'string') {
        const deps = (formula.match(/\{\{(\w+)\}\}/g) || []).map((m: string) => m.replace(/\{\{|\}\}/g, ''));
        const hasRaw = deps.length > 0 && deps.every((d: string) => rows.some((r: any) => r.hasOwnProperty(d)));
        if (hasRaw && deps.length >= 2) {
          const sums: Record<string, number> = {};
          deps.forEach((d: string) => { sums[d] = rows.reduce((s: number, r: any) => s + (Number(r[d]) || 0), 0); });
          let expr = formula;
          deps.forEach((d: string) => { expr = expr.replace(new RegExp(`\\{\\{${d}\\}\\}`, 'g'), String(sums[d])); });
          try {
            const sanitized = expr.replace(/[^0-9+\-*/().  ]/g, '');
            kpiValue = Function('"use strict"; return (' + sanitized + ')')();
            if (!isFinite(kpiValue)) kpiValue = 0;
          } catch { kpiValue = 0; }
        } else {
          const vals = rows.map((r: any) => Number(r[primaryMetric]) || 0).filter((v: number) => !isNaN(v));
          kpiValue = aggregation === 'sum' ? vals.reduce((s: number, v: number) => s + v, 0)
            : aggregation === 'avg' && vals.length ? vals.reduce((s: number, v: number) => s + v, 0) / vals.length
              : aggregation === 'min' && vals.length ? Math.min(...vals)
                : aggregation === 'max' && vals.length ? Math.max(...vals)
                  : aggregation === 'count' ? vals.length : 0;
        }
      } else {
        const vals = rows.map((r: any) => Number(r[primaryMetric]) || 0).filter((v: number) => !isNaN(v));
        kpiValue = aggregation === 'sum' ? vals.reduce((s: number, v: number) => s + v, 0)
          : aggregation === 'avg' && vals.length ? vals.reduce((s: number, v: number) => s + v, 0) / vals.length
            : aggregation === 'min' && vals.length ? Math.min(...vals)
              : aggregation === 'max' && vals.length ? Math.max(...vals)
                : aggregation === 'count' ? vals.length : 0;
      }
    } else {
      kpiValue = visualizationData[0]?.[primaryMetric] || 0;
    }

    const trendValue = visualizationData[0]?.[vizConfig?.trendField] || 0;
    const currentValue = kpiValue;
    let variancePercent = 0;
    let previousValue = currentValue;

    if (previousRows.length > 0 && primaryMetric) {
      try {
        const otFilter = vizConfig?.orderTypeFilter;
        let prevAgg = 0;
        if (vizConfig?.mode === 'orderTypeMix' && otFilter) {
          const prevSalesForType = previousRows.reduce((s: number, r: any) => s + getNetSalesForOrderType(r, otFilter), 0);
          const prevSalesTotal = previousRows.reduce((s: number, r: any) => s + (Number(r.net_sales) || 0), 0);
          prevAgg = prevSalesTotal > 0 ? (prevSalesForType / prevSalesTotal) * 100 : 0;
        } else {
          const prevVals = previousRows.map((r: any) => Number(r[primaryMetric]) || 0).filter((v: number) => !isNaN(v));
          prevAgg = aggregation === 'sum' ? prevVals.reduce((s: number, v: number) => s + v, 0)
            : aggregation === 'avg' && prevVals.length ? prevVals.reduce((s: number, v: number) => s + v, 0) / prevVals.length : 0;
        }
        if (prevAgg !== 0) { variancePercent = ((currentValue - prevAgg) / prevAgg) * 100; previousValue = prevAgg; }
      } catch { }
    }

    let kpiSubtitle = description;
    if (vizConfig?.mode === 'calculated' && vizConfig?.rankingType && visualizationData.length > 0) {
      const loc = visualizationData[0]?._selectedLocationName;
      if (loc) kpiSubtitle = loc;
    }

    return { kpiValue, trendValue, varianceData: { value: variancePercent, currentValue, previousValue, isSingleDay }, kpiSubtitle };
  }, [partitionedData, visualizationData, vizConfig, primaryMetric, aggregation]);

  // ─── SPARKLINE (uses currentRows from partition — no additional scan) ─────────
  const sparklineDataPoints = useMemo(() => {
    const { currentRows, minDate, maxDate } = partitionedData;
    if (!currentRows.length) return undefined;

    const rules = vizConfig?.timeAxisRules || { daily: { maxDays: 30 }, weekly: { maxDays: 180 }, monthly: { maxDays: 365 } };

    try {
      const isOrderTypeMixMode = vizConfig?.mode === 'orderTypeMix' && vizConfig?.orderTypeFilter;
      const isFormulaMode = vizConfig?.mode === 'formula';
      const formula = vizConfig?.formula;

      if (isOrderTypeMixMode) {
        const otf = vizConfig.orderTypeFilter;
        const result = TimeGroupingService.processChartDataWithDimensions(currentRows, minDate, maxDate, ['net_sales'], 'sum', ['order_type'], rules);
        const raw = result.data.map((row: any) => {
          const salesForType = getNetSalesForOrderType(row, otf);
          let total = row.hasOwnProperty('net_sales') ? Number(row.net_sales) || 0 : 0;
          if (!total) for (const [k, v] of Object.entries(row || {})) {
            if (!['date', 'dateFormatted', 'timeKey', 'time', 'id', 'recordCount'].includes(k)) { const n = Number(v); if (!isNaN(n)) total += n; }
          }
          const dv = row.dateFormatted || row.date || row.timeKey || row.time;
          return dv ? { value: total > 0 ? (salesForType / total) * 100 : 0, date: dv } : null;
        }).filter((i: any): i is { value: number; date: string } => i != null);
        return ensureOptimalSparklinePoints(raw, 6, 10);
      } else if (isFormulaMode && formula) {
        const deps = (formula.match(/\{\{(\w+)\}\}/g) || []).map((m: string) => m.replace(/\{\{|\}\}/g, ''));
        const result = TimeGroupingService.processChartDataWithDimensions(currentRows, minDate, maxDate, deps, 'sum', undefined, rules);
        const raw = result.data.map((row: any) => {
          let expr = formula;
          deps.forEach((d: string) => { expr = expr.replace(new RegExp(`\\{\\{${d}\\}\\}`, 'g'), String(Number(row[d]) || 0)); });
          let val = 0;
          try { const s = expr.replace(/[^0-9+\-*/().  ]/g, ''); val = Function('"use strict"; return (' + s + ')')(); } catch { }
          const dv = row.dateFormatted || row.date || row.timeKey || row.time;
          return dv ? { value: val, date: dv } : null;
        }).filter((i: any): i is { value: number; date: string } => i != null);
        return ensureOptimalSparklinePoints(raw, 6, 10);
      } else {
        const result = TimeGroupingService.processChartDataWithDimensions(currentRows, minDate, maxDate, [primaryMetric], aggregation, undefined, rules);
        const raw = result.data.map((row: any) => {
          const v = row[primaryMetric];
          const dv = row.dateFormatted || row.date || row.timeKey || row.time;
          return dv ? { value: typeof v === 'number' ? v : parseFloat(String(v)) || 0, date: dv } : null;
        }).filter((i: any): i is { value: number; date: string } => i != null);
        return ensureOptimalSparklinePoints(raw, 6, 10);
      }
    } catch (error) {
      console.warn(`[KPI Sparkline] Failed for ${visualization.id}:`, error);
      return undefined;
    }
  }, [partitionedData, vizConfig, primaryMetric, aggregation]);

  return (
    <KpiComponent
      title={title}
      value={kpiProps.kpiValue}
      format={vizConfig?.format}
      prefix={vizConfig?.prefix}
      suffix={vizConfig?.suffix}
      subtitle={kpiProps.kpiSubtitle}
      trend={vizConfig?.showTrend ? {
        value: kpiProps.trendValue,
        direction: kpiProps.trendValue > 0 ? 'up' : kpiProps.trendValue < 0 ? 'down' : 'neutral',
        period: vizConfig?.comparisonPeriod || 'vs. previous period'
      } : undefined}
      variance={kpiProps.varianceData}
      sparklineData={sparklineDataPoints}
      tallHeight={tallHeight}
      cardCount={cardCount}
      {...commonProps}
    />
  );
});


// Debug logging utility - only logs in development mode
const DEBUG = process.env.NODE_ENV === 'development';
const debugLog = (message: string, ...args: any[]) => {
  if (DEBUG) {
    // console.log(message, ...args);
  }
};

/**
 * VisualizationDecorator - Adds edit button overlay in preview mode
 */
const VisualizationDecorator: React.FC<{
  visualization: AnalyticsVisualization;
  isPreviewMode: boolean;
  onEdit: (visualization: AnalyticsVisualization) => void;
  children: React.ReactNode;
}> = ({ visualization, isPreviewMode, onEdit, children }) => {
  if (!isPreviewMode) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        '&:hover .preview-edit-button': {
          opacity: 1,
        },
      }}
    >
      {children}
      <IconButton
        className="preview-edit-button"
        onClick={() => onEdit(visualization)}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: 'primary.main',
          opacity: 0,
          transition: 'opacity 0.2s',
          zIndex: 10,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
          },
        }}
        size="small"
        title="Edit Properties"
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

/**
 * DeferredTableRenderer - Renders heavy tables with a slight delay for better UX
 */
const DeferredTableRenderer: React.FC<{
  visualization: any;
  visualizationData: any;
  modifiedConfig: any;
  commonProps: any;
  isHeavy: boolean;
  filterValues?: Record<string, any>;
  dateFilterId?: string;
  allVisualizationData?: any; // Full dataset for variance calculations
  highlightDeviations: boolean;
  setHighlightDeviations: (value: boolean) => void;
  varianceType: 'YoY' | 'MoM' | 'WoW';
  setVarianceType: (value: 'YoY' | 'MoM' | 'WoW') => void;
  varianceDisplayMode: 'absolute' | 'percent';
  setVarianceDisplayMode: (value: 'absolute' | 'percent') => void;
  isVarianceRecalculating?: boolean;
  onVarianceTypeChange?: (value: 'YoY' | 'MoM' | 'WoW') => void;
  onVarianceDisplayModeChange?: (value: 'absolute' | 'percent') => void;
  showCreditCardDetail: boolean;
  setShowCreditCardDetail: (value: boolean) => void;
  showThirdPartyDetail: boolean;
  setShowThirdPartyDetail: (value: boolean) => void;
  showAllPaymentDetails: boolean;
  setShowAllPaymentDetails: (value: boolean) => void;
}> = ({
  visualization,
  visualizationData,
  modifiedConfig,
  commonProps,
  isHeavy,
  filterValues = {},
  dateFilterId,
  allVisualizationData,
  highlightDeviations,
  setHighlightDeviations,
  varianceType,
  setVarianceType,
  varianceDisplayMode,
  setVarianceDisplayMode,
  isVarianceRecalculating = false,
  onVarianceTypeChange,
  onVarianceDisplayModeChange,
  showCreditCardDetail,
  setShowCreditCardDetail,
  showThirdPartyDetail,
  setShowThirdPartyDetail,
  showAllPaymentDetails,
  setShowAllPaymentDetails
}) => {
    const [shouldRender, setShouldRender] = useState(!isHeavy);
    const [shouldRenderVariance, setShouldRenderVariance] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const { title, description } = visualization;

    // Get date filter for calculating date range duration
    const dateFilter = filterValues[dateFilterId || 'global-date-range'];

    // Helper function to check if MoM comparison is valid (same days exist in previous month)
    const isMoMValid = React.useCallback((start: Date, end: Date): boolean => {
      try {
        // Get day of month for start and end dates
        const startDay = start.getDate();
        const endDay = end.getDate();

        // Calculate previous month dates (same day of month)
        const momStart = new Date(start);
        momStart.setMonth(momStart.getMonth() - 1);

        const momEnd = new Date(end);
        momEnd.setMonth(momEnd.getMonth() - 1);

        // Check if the dates rolled over to a different month (invalid)
        // This happens when the previous month doesn't have enough days
        // Example: Jan 31 → Feb doesn't have 31st, so it rolls to Mar 3
        const startRolledOver = momStart.getMonth() !== (start.getMonth() - 1 + 12) % 12;
        const endRolledOver = momEnd.getMonth() !== (end.getMonth() - 1 + 12) % 12;

        // Also check if the day-of-month matches (handles edge cases)
        const startDayMatches = momStart.getDate() === startDay;
        const endDayMatches = momEnd.getDate() === endDay;

        // MoM is invalid if dates rolled over OR day-of-month doesn't match
        if (startRolledOver || endRolledOver || !startDayMatches || !endDayMatches) {
          return false;
        }

        return true;
      } catch (error) {
        console.warn('[MoM Validation] Error checking MoM validity:', error);
        return false;
      }
    }, []);

    // Calculate date range duration in days
    const dateRangeDays = React.useMemo(() => {
      if (!dateFilter?.start || !dateFilter?.end) return 0;

      const start = dateFilter.start instanceof Date ? dateFilter.start : new Date(dateFilter.start);
      const end = dateFilter.end instanceof Date ? dateFilter.end : new Date(dateFilter.end);

      const diffMs = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1; // +1 to include end date

      return diffDays;
    }, [dateFilter]);

    // Determine which comparison modes should be disabled based on date range
    const isWoWDisabled = dateRangeDays > 7;

    // MoM is disabled if:
    // 1. Date range > 31 days (can't span more than a month)
    // 2. The same days don't exist in the previous month (e.g., Jan 31 → Feb has no 31st)
    const isMoMDisabled = React.useMemo(() => {
      if (dateRangeDays > 31) return true;
      if (!dateFilter?.start || !dateFilter?.end) return true;

      const start = dateFilter.start instanceof Date ? dateFilter.start : new Date(dateFilter.start);
      const end = dateFilter.end instanceof Date ? dateFilter.end : new Date(dateFilter.end);

      return !isMoMValid(start, end);
    }, [dateFilter, dateRangeDays, isMoMValid]);

    const isYoYDisabled = dateRangeDays > 365;

    // Auto-switch to a valid comparison type if current selection becomes disabled
    React.useEffect(() => {
      if (highlightDeviations) {
        if (varianceType === 'WoW' && isWoWDisabled) {
          if (!isMoMDisabled) setVarianceType('MoM');
          else if (!isYoYDisabled) setVarianceType('YoY');
        } else if (varianceType === 'MoM' && isMoMDisabled) {
          if (!isYoYDisabled) setVarianceType('YoY');
          else if (!isWoWDisabled) setVarianceType('WoW');
        } else if (varianceType === 'YoY' && isYoYDisabled) {
          if (!isWoWDisabled) setVarianceType('WoW');
          else if (!isMoMDisabled) setVarianceType('MoM');
        }
      }
    }, [highlightDeviations, varianceType, isWoWDisabled, isMoMDisabled, isYoYDisabled]);

    const isHierarchical = modifiedConfig?.hierarchical;
    const hasExpandableRows = isHierarchical && Array.isArray(visualizationData) &&
      visualizationData.some((row: any) => row.hasChildren);

    // Count expandable rows to determine if all are expanded
    const expandableRowCount = React.useMemo(() => {
      if (!isHierarchical || !Array.isArray(visualizationData)) return 0;
      return visualizationData.filter((row: any) => row.hasChildren).length;
    }, [isHierarchical, visualizationData]);

    useEffect(() => {
      if (isHeavy && !shouldRender) {
        // Defer heavy tables by 50ms to let UI respond first
        const timer = setTimeout(() => setShouldRender(true), 50);
        return () => clearTimeout(timer);
      }
    }, [isHeavy, shouldRender]);

    // Log when isVarianceRecalculating prop changes
    React.useEffect(() => {
      console.log('🔄 [DeferredTableRenderer] isVarianceRecalculating prop changed:', isVarianceRecalculating);
    }, [isVarianceRecalculating]);

    // Control variance calculation rendering when recalculation is happening
    useEffect(() => {
      if (highlightDeviations && isVarianceRecalculating) {
        console.log('🔄 [Variance Loading] Setting shouldRenderVariance to false - blocking variance calculations');
        // Temporarily prevent variance calculations from rendering
        setShouldRenderVariance(false);

        // Use requestAnimationFrame to allow UI to update first
        const rafId = requestAnimationFrame(() => {
          console.log('🔄 [Variance Loading] RAF callback - re-enabling variance rendering');
          // Re-enable variance rendering after UI updates
          setTimeout(() => {
            setShouldRenderVariance(true);
            console.log('🔄 [Variance Loading] Variance rendering re-enabled');
          }, 0);
        });

        return () => {
          console.log('🔄 [Variance Loading] Cleaning up RAF');
          cancelAnimationFrame(rafId);
        };
      } else {
        // Ensure variance rendering is enabled when not recalculating
        if (!shouldRenderVariance) {
          console.log('🔄 [Variance Loading] Re-enabling variance rendering (not recalculating)');
          setShouldRenderVariance(true);
        }
      }
    }, [isVarianceRecalculating, highlightDeviations, shouldRenderVariance]);

    // Filter data based on search query
    const filteredData = React.useMemo(() => {
      if (!searchQuery.trim()) {
        return Array.isArray(visualizationData) ? visualizationData : [];
      }

      const query = searchQuery.toLowerCase();
      return (Array.isArray(visualizationData) ? visualizationData : []).filter((row: any) => {
        // Search in location field and other string fields
        const location = row.location || row.location_name || row.name || '';
        const searchableText = Object.values(row)
          .filter(val => typeof val === 'string')
          .join(' ')
          .toLowerCase();

        return location.toLowerCase().includes(query) || searchableText.includes(query);
      });
    }, [visualizationData, searchQuery]);

    // Helper function to resolve date constants
    const resolveDate = React.useCallback((dateValue: any): Date => {
      if (!dateValue) return new Date();
      if (dateValue instanceof Date) return dateValue;
      if (typeof dateValue === 'string') {
        if (DateConstantsService.isDateConstant(dateValue)) {
          const resolvedDateString = DateConstantsService.resolveDateConstant(dateValue);
          return new Date(resolvedDateString);
        }
        return new Date(dateValue);
      }
      return new Date(dateValue);
    }, []);

    // Deviations function - calculates REAL deviation based on selected comparison type (WoW/MoM/YoY)
    // Returns DeviationResult with both absolute and percentage changes
    // Uses deferred variance type to prevent UI blocking during calculations
    const calculateDeviation = React.useCallback((row: any, column: any, value: any): any => {
      if (!highlightDeviations) return null;

      // Skip non-numeric columns and the first column (usually location/name)
      if (typeof value !== 'number' || column.expandIcon) return null;

      // Skip cells with value of $0
      if (value === 0) return null;

      try {
        // Get the metric field we're calculating variance for
        const metricField = column.field;

        // Use variance type directly for calculations
        const currentVarianceType = varianceType;

        // APPROACH 1: Check if backend provided comparison fields directly in row
        // (e.g., net_sales_wow, net_sales_mom, net_sales_yoy)
        let comparisonField: string | null = null;
        let comparisonValue: number | null = null;

        // Construct comparison field name based on deferred variance type
        switch (currentVarianceType) {
          case 'WoW':
            comparisonField = `${metricField}_wow`;
            break;
          case 'MoM':
            comparisonField = `${metricField}_mom`;
            break;
          case 'YoY':
            comparisonField = `${metricField}_yoy`;
            break;
        }

        // Try to get comparison value from the row data
        if (comparisonField && row[comparisonField] !== undefined && row[comparisonField] !== null) {
          comparisonValue = Number(row[comparisonField]);

          // Debug log (first occurrence only)
          if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
            console.log(`[Variance] Found comparison field "${comparisonField}" in row data:`, {
              metricField,
              value,
              comparisonValue,
              varianceType: currentVarianceType
            });
          }
        } else {
          // Try alternative naming conventions
          const altFields = [
            `${metricField}_previous`,
            `${metricField}_prior`,
            `${metricField}_last_${currentVarianceType.toLowerCase()}`,
            `previous_${metricField}`,
            `prior_${metricField}`,
            `${metricField}_prev`,
            `prev_${metricField}`
          ];

          for (const altField of altFields) {
            if (row[altField] !== undefined && row[altField] !== null) {
              comparisonValue = Number(row[altField]);

              // Debug log (first occurrence only)
              if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
                console.log(`[Variance] Found alternative comparison field "${altField}":`, {
                  metricField,
                  value,
                  comparisonValue,
                  varianceType: currentVarianceType
                });
              }
              break;
            }
          }

          // Debug log if no comparison field found (sample 1%)
          if (comparisonValue === null && process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
            console.log(`[Variance] No comparison field found for "${metricField}" (${currentVarianceType})`, {
              availableFields: Object.keys(row),
              triedFields: [comparisonField, ...altFields]
            });
          }
        }

        // APPROACH 2: If no comparison field exists, try to calculate from full dataset
        // This works if we have the full time-series data available
        if ((comparisonValue === null || comparisonValue === undefined || isNaN(comparisonValue)) && allVisualizationData) {
          // Check if we have access to full time-series dataset
          const fullDataset = Array.isArray(allVisualizationData) ? allVisualizationData : visualizationData;

          // Check if this is time-series data (has date fields)
          const hasTimeSeriesData = fullDataset && fullDataset.length > 0 &&
            fullDataset.some((r: any) => r.date || r.dateFormatted || r.timeKey || r.time);

          if (hasTimeSeriesData && dateFilter?.start && dateFilter?.end) {
            // Get location identifier
            const locationId = row.id || row.location_id || row.location || row.location_name || row.name || '';

            // Calculate comparison period dates
            const currentStart = resolveDate(dateFilter.start);
            const currentEnd = resolveDate(dateFilter.end);

            let comparisonStart: Date;
            let comparisonEnd: Date;

            switch (currentVarianceType) {
              case 'WoW':
                // 7 days back
                comparisonStart = new Date(currentStart);
                comparisonStart.setDate(comparisonStart.getDate() - 7);
                comparisonEnd = new Date(currentEnd);
                comparisonEnd.setDate(comparisonEnd.getDate() - 7);
                break;
              case 'MoM':
                // Calendar month alignment: same days of the month, previous month
                // Example: Nov 1-10 → Oct 1-10
                comparisonStart = new Date(currentStart);
                comparisonStart.setMonth(comparisonStart.getMonth() - 1);
                comparisonEnd = new Date(currentEnd);
                comparisonEnd.setMonth(comparisonEnd.getMonth() - 1);

                // Validate MoM dates (check for rollover like Jan 31 → Feb)
                const momStartDay = currentStart.getDate();
                const momEndDay = currentEnd.getDate();
                const momStartValid = comparisonStart.getDate() === momStartDay &&
                  comparisonStart.getMonth() === (currentStart.getMonth() - 1 + 12) % 12;
                const momEndValid = comparisonEnd.getDate() === momEndDay &&
                  comparisonEnd.getMonth() === (currentEnd.getMonth() - 1 + 12) % 12;

                // If MoM dates are invalid, return null (no comparison available)
                if (!momStartValid || !momEndValid) {
                  return null;
                }
                break;
              case 'YoY':
                // 365 days back (1 year)
                comparisonStart = new Date(currentStart);
                comparisonStart.setFullYear(comparisonStart.getFullYear() - 1);
                comparisonEnd = new Date(currentEnd);
                comparisonEnd.setFullYear(comparisonEnd.getFullYear() - 1);
                break;
              default:
                return null;
            }

            const comparisonStartStr = comparisonStart.toISOString().split('T')[0];
            const comparisonEndStr = comparisonEnd.toISOString().split('T')[0];

            // Filter data for comparison period and matching location
            const comparisonData = fullDataset.filter((r: any) => {
              const rowDate = r.date || r.dateFormatted || r.timeKey || r.time;
              if (!rowDate) return false;

              const rowDateStr = typeof rowDate === 'string'
                ? (rowDate.includes('T') ? rowDate.split('T')[0] : rowDate)
                : new Date(rowDate).toISOString().split('T')[0];

              // Check date range
              const inDateRange = rowDateStr >= comparisonStartStr && rowDateStr <= comparisonEndStr;

              // Check location match (for hierarchical data)
              const rowLocationId = r.id || r.location_id || r.location || r.location_name || r.name || '';
              const locationMatches = !locationId || rowLocationId === locationId;

              return inDateRange && locationMatches;
            });

            // Aggregate comparison period data
            if (comparisonData.length > 0) {
              const comparisonValues = comparisonData
                .map((r: any) => Number(r[metricField]) || 0)
                .filter((v: number) => !isNaN(v));

              if (comparisonValues.length > 0) {
                // Use sum aggregation (matching KPI logic)
                comparisonValue = comparisonValues.reduce((sum, v) => sum + v, 0);

                // Debug log (sample 1%)
                if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
                  console.log(`[Variance] Calculated from time-series data:`, {
                    metricField,
                    varianceType: currentVarianceType,
                    currentValue: value,
                    comparisonValue,
                    comparisonDataPoints: comparisonData.length,
                    comparisonPeriod: `${comparisonStartStr} to ${comparisonEndStr}`
                  });
                }
              }
            } else if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
              console.log(`[Variance] No time-series comparison data found:`, {
                metricField,
                varianceType: currentVarianceType,
                comparisonPeriod: `${comparisonStartStr} to ${comparisonEndStr}`,
                fullDatasetLength: fullDataset.length
              });
            }
          }
        }

        // If we still don't have a comparison value, return null
        if (comparisonValue === null || comparisonValue === undefined || isNaN(comparisonValue)) {
          return null;
        }

        // Skip if comparison value is 0 (can't calculate percentage)
        if (comparisonValue === 0) {
          return null;
        }

        // Calculate both absolute and percentage changes
        const deltaAbsolute = value - comparisonValue;
        const deltaPercent = (deltaAbsolute / comparisonValue) * 100;

        // Filter out very small changes (less than 1% and less than $10)
        if (Math.abs(deltaPercent) < 1 && Math.abs(deltaAbsolute) < 10) {
          return null;
        }

        // Return DeviationResult object with both metrics
        return {
          deltaPercent,
          deltaAbsolute,
          currentValue: value,
          previousValue: comparisonValue
        };

      } catch (error) {
        console.warn('[calculateDeviation] Error calculating variance:', error);
        return null;
      }
    }, [highlightDeviations, varianceType, allVisualizationData, visualizationData, dateFilter, resolveDate]);


    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Controls Row: Search, Export */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            px: 0,
          }}
        >
          {/* Search Bar */}
          <Box
            sx={{
              flex: 1,
              position: 'relative',
            }}
          >
            <TextField
              placeholder="Search Locations"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    <SearchIcon sx={{ fontSize: 20, color: '#71717a' }} />
                  </Box>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  border: '1px solid #e4e4e7',
                  borderRadius: '6px',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  '&:hover': {
                    borderColor: '#18181b',
                  },
                  '&.Mui-focused': {
                    borderColor: '#18181b',
                  },
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '8px 12px',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  color: '#18181b',
                },
              }}
            />
          </Box>

          {/* Payment Channel Detail Toggles - Only show for order-type-detail-table */}
          {visualization.id === 'order-type-detail-table' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* Detailed/Simplified Button */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const willExpand = !showAllPaymentDetails;
                  setShowAllPaymentDetails(willExpand);
                  setShowCreditCardDetail(willExpand);
                  setShowThirdPartyDetail(willExpand);
                }}
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '11px',
                  fontWeight: 400,
                  textTransform: 'none',
                  borderColor: '#e4e4e7',
                  color: showAllPaymentDetails ? '#f97316' : '#18181b',
                  '&:hover': {
                    borderColor: '#f97316',
                    backgroundColor: 'rgba(249, 115, 22, 0.04)',
                  },
                  minWidth: '60px',
                  height: '24px',
                  padding: '2px 6px',
                }}
              >
                {showAllPaymentDetails ? 'Simplified' : 'Detailed'}
              </Button>

              {/* Credit Card Detail Toggle */}
              <Tooltip
                title="Expand to view individual credit card brands (Visa, MasterCard, Amex, Discover, Other Cards, Card Failed)"
                arrow
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showCreditCardDetail}
                      onChange={(e) => {
                        const val = e.target.checked;
                        setShowCreditCardDetail(val);
                        if (val && showThirdPartyDetail) setShowAllPaymentDetails(true);
                        if (!val) setShowAllPaymentDetails(false);
                      }}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase': {
                          padding: '4px',
                          '&.Mui-checked': {
                            color: '#ffffff',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#f97316',
                              opacity: 1,
                            },
                          },
                          '&.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#f97316',
                          },
                        },
                        '& .MuiSwitch-thumb': {
                          backgroundColor: '#ffffff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          width: '14px',
                          height: '14px',
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: '#e4e4e7',
                          opacity: 1,
                          height: '10px',
                          borderRadius: '10px',
                          width: '32px',
                        },
                      }}
                    />
                  }
                  label="Credit Card Detail"
                  sx={{
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '11px',
                      fontWeight: 400,
                      color: showCreditCardDetail ? '#f97316' : '#18181b',
                      marginLeft: '3px',
                    },
                  }}
                />
              </Tooltip>

              {/* Third-Party Delivery Detail Toggle */}
              <Tooltip
                title="Expand to view individual delivery platforms (Uber Eats, DoorDash, Grubhub, Postmates, Other Third-Party)"
                arrow
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showThirdPartyDetail}
                      onChange={(e) => {
                        const val = e.target.checked;
                        setShowThirdPartyDetail(val);
                        if (val && showCreditCardDetail) setShowAllPaymentDetails(true);
                        if (!val) setShowAllPaymentDetails(false);
                      }}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase': {
                          padding: '4px',
                          '&.Mui-checked': {
                            color: '#ffffff',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#dc2626',
                              opacity: 1,
                            },
                          },
                          '&.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#dc2626',
                          },
                        },
                        '& .MuiSwitch-thumb': {
                          backgroundColor: '#ffffff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          width: '14px',
                          height: '14px',
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: '#e4e4e7',
                          opacity: 1,
                          height: '10px',
                          borderRadius: '10px',
                          width: '32px',
                        },
                      }}
                    />
                  }
                  label="Third-Party Detail"
                  sx={{
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '11px',
                      fontWeight: 400,
                      color: showThirdPartyDetail ? '#dc2626' : '#18181b',
                      marginLeft: '3px',
                    },
                  }}
                />
              </Tooltip>
            </Box>
          )}

          {/* Highlight Variances Toggle */}
          <Tooltip
            title={`Activate this toggle to view Calendar Comparison variances for the selected time frame versus: the same period Last Year (YoY), Last Month (MoM), or Last Week (WoW). Current date range: ${dateRangeDays} day${dateRangeDays !== 1 ? 's' : ''}.`}
            arrow
            placement="top"
          >
            <FormControlLabel
              control={
                <Switch
                  checked={highlightDeviations}
                  onChange={(e) => setHighlightDeviations(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase': {
                      '&.Mui-checked': {
                        color: '#ffffff',
                        '& + .MuiSwitch-track': {
                          backgroundColor: '#f97316',
                          opacity: 1,
                        },
                      },
                      '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#f97316',
                      },
                    },
                    '& .MuiSwitch-thumb': {
                      backgroundColor: '#ffffff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#e4e4e7',
                      opacity: 1,
                    },
                  }}
                />
              }
              label="Highlight Variances"
              sx={{
                marginRight: '16px',
                '& .MuiFormControlLabel-label': {
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  fontWeight: 400,
                  color: highlightDeviations ? '#f97316' : '#18181b',
                },
              }}
            />
          </Tooltip>

          {/* Variance Type Radio Buttons */}
          <>
            <Box
              sx={{
                height: '24px',
                width: '1px',
                backgroundColor: '#e4e4e7',
                marginRight: '16px',
              }}
            />
            <RadioGroup
              row
              value={varianceType}
              onChange={(e) => {
                const value = e.target.value as 'YoY' | 'MoM' | 'WoW';
                if (onVarianceTypeChange) {
                  onVarianceTypeChange(value);
                } else {
                  setVarianceType(value);
                }
              }}
              sx={{
                opacity: highlightDeviations ? 1 : 0.5,
                pointerEvents: highlightDeviations ? 'auto' : 'none',
                '& .MuiRadio-root': {
                  padding: '4px',
                  color: highlightDeviations ? '#d4d4d8' : '#d4d4d8',
                  '&.Mui-checked': {
                    color: highlightDeviations ? '#f97316' : '#d4d4d8',
                  },
                  '&.Mui-disabled': {
                    color: '#e4e4e7',
                  },
                },
                '& .MuiFormControlLabel-root': {
                  marginRight: '16px',
                  '&:last-child': {
                    marginRight: 0,
                  },
                },
              }}
            >
              <Tooltip
                title={isYoYDisabled ? `YoY disabled: date range exceeds 365 days (current: ${dateRangeDays} days)` : 'Year-over-Year: Compare to same period last year'}
                arrow
              >
                <FormControlLabel
                  value="YoY"
                  control={<Radio />}
                  label="YoY"
                  disabled={isYoYDisabled}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '14px',
                      fontWeight: 400,
                      color: isYoYDisabled ? '#e4e4e7' : (highlightDeviations && varianceType === 'YoY' ? '#f97316' : '#d4d4d8'),
                    },
                  }}
                />
              </Tooltip>
              <Tooltip
                title={isMoMDisabled
                  ? `MoM disabled: ${dateRangeDays > 31
                    ? `date range exceeds 31 days (current: ${dateRangeDays} days)`
                    : `selected dates don't exist in previous month (e.g., Jan 31 → Feb has no 31st)`}`
                  : 'Month-over-Month: Compare to same days of the previous month (e.g., Nov 1-10 → Oct 1-10)'}
                arrow
              >
                <FormControlLabel
                  value="MoM"
                  control={<Radio />}
                  label="MoM"
                  disabled={isMoMDisabled}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '14px',
                      fontWeight: 400,
                      color: isMoMDisabled ? '#e4e4e7' : (highlightDeviations && varianceType === 'MoM' ? '#f97316' : '#d4d4d8'),
                    },
                  }}
                />
              </Tooltip>
              <Tooltip
                title={isWoWDisabled ? `WoW disabled: date range exceeds 7 days (current: ${dateRangeDays} days)` : 'Week-over-Week: Compare to same period last week'}
                arrow
              >
                <FormControlLabel
                  value="WoW"
                  control={<Radio />}
                  label="WoW"
                  disabled={isWoWDisabled}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '14px',
                      fontWeight: 400,
                      color: isWoWDisabled ? '#e4e4e7' : (highlightDeviations && varianceType === 'WoW' ? '#f97316' : '#d4d4d8'),
                    },
                  }}
                />
              </Tooltip>
            </RadioGroup>
          </>

          {/* Variance Display Mode Toggle ($/%) */}
          {highlightDeviations && (
            <>
              <Box
                sx={{
                  height: '24px',
                  width: '1px',
                  backgroundColor: '#e4e4e7',
                  marginLeft: '16px',
                  marginRight: '16px',
                }}
              />
              <Tooltip
                title="Toggle between absolute dollar change (Δ$) and percentage change (Δ%). Heat map coloring always uses percentage for fair comparison across locations."
                arrow
                placement="top"
              >
                <RadioGroup
                  row
                  value={varianceDisplayMode}
                  onChange={(e) => {
                    const value = e.target.value as 'absolute' | 'percent';
                    if (onVarianceDisplayModeChange) {
                      onVarianceDisplayModeChange(value);
                    } else {
                      setVarianceDisplayMode(value);
                    }
                  }}
                  sx={{
                    '& .MuiRadio-root': {
                      padding: '4px',
                      color: '#e4e4e7',
                      '&.Mui-checked': {
                        color: '#f97316',
                      },
                    },
                    '& .MuiFormControlLabel-root': {
                      marginRight: '16px',
                      '&:last-child': {
                        marginRight: 0,
                      },
                    },
                  }}
                >
                  <FormControlLabel
                    value="absolute"
                    control={<Radio />}
                    label="Δ$"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        fontWeight: 600,
                        color: varianceDisplayMode === 'absolute' ? '#f97316' : '#71717a',
                      },
                    }}
                  />
                  <FormControlLabel
                    value="percent"
                    control={<Radio />}
                    label="Δ%"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        fontWeight: 600,
                        color: varianceDisplayMode === 'percent' ? '#f97316' : '#71717a',
                      },
                    }}
                  />
                </RadioGroup>
              </Tooltip>
            </>
          )}

          {/* Export Button */}
          <ExportButton
            data={filteredData}
            columns={modifiedConfig?.columns || []}
            filename={`${title?.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}`}
            highlightDeviations={highlightDeviations}
            calculateDeviation={calculateDeviation}
            varianceDisplayMode={varianceDisplayMode}
          />
        </Box>

        {/* Table */}
        {shouldRender ? (
          <Box sx={{
            flex: 1,
            position: 'relative'
          }}>
            {/* Explanatory text for order-type-detail-table */}
            {visualization.id === 'order-type-detail-table' && (
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '13px',
                  color: '#71717a',
                  marginBottom: 1.5,
                  lineHeight: 1.5,
                }}
              >
                Net Sales (green) reflect item revenue after discounts/voids/refunds. Payments Collected (blue) reflect total tender amounts including tax, tip, and fees.
              </Typography>
            )}
            <AnalyticsTable
              data={filteredData}
              config={(() => {
                const finalConfig = modifiedConfig || { columns: [], hierarchical: false };
                return finalConfig;
              })()}
              highlightDeviations={highlightDeviations}
              calculateDeviation={calculateDeviation}
              varianceDisplayMode={varianceDisplayMode}
              shouldRenderVariance={shouldRenderVariance}
              showCreditCardDetail={showCreditCardDetail}
              showThirdPartyDetail={showThirdPartyDetail}
              showAllPaymentDetails={showAllPaymentDetails}
              onCreditCardDetailChange={setShowCreditCardDetail}
              onThirdPartyDetailChange={setShowThirdPartyDetail}
              {...commonProps}
              loading={(() => {
                // Override commonProps.loading with variance loading state (variance loading takes precedence)
                const finalLoading = isVarianceRecalculating || commonProps?.loading;
                console.log('🔄 [DeferredTableRenderer] Passing loading prop to AnalyticsTable:', {
                  isVarianceRecalculating,
                  commonPropsLoading: commonProps?.loading,
                  finalLoading
                });
                return finalLoading;
              })()}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
            <CircularProgress size={24} sx={{ mr: 2, color: '#f97316' }} />
            <Typography variant="body2" color="textSecondary">
              Building table structure...
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

/**
 * Utility function to normalize string objects back to primitive strings
 * This fixes the issue where strings get converted to String objects
 * which appear as character arrays {0: 'c', 1: 'a', 2: 's', ...}
 */
// Helper functions for filter persistence
function serializeFilterValue(value: any): any {
  if (value === null || value === undefined) {
    return value;
  }

  // Handle Date objects - convert to ISO string only if valid
  if (value instanceof Date) {
    // Check if date is valid before serializing
    if (isNaN(value.getTime())) {
      return null; // Invalid date becomes null
    }
    return value.toISOString();
  }

  // Handle date range objects
  if (typeof value === 'object' && 'start' in value && 'end' in value) {
    const result: any = {
      start: value.start instanceof Date
        ? (isNaN(value.start.getTime()) ? null : value.start.toISOString())
        : value.start, // Preserve date constants like "LAST_QUARTER" as strings
      end: value.end instanceof Date
        ? (isNaN(value.end.getTime()) ? null : value.end.toISOString())
        : value.end, // Preserve date constants like "TODAY" as strings
    };
    // Preserve other properties like startTime, endTime
    if (value.startTime !== undefined) result.startTime = value.startTime;
    if (value.endTime !== undefined) result.endTime = value.endTime;
    return result;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.map(item => serializeFilterValue(item));
  }

  // Handle objects
  if (typeof value === 'object') {
    const serialized: any = {};
    for (const [key, val] of Object.entries(value)) {
      serialized[key] = serializeFilterValue(val);
    }
    return serialized;
  }

  return value;
}

function deserializeFilterValue(value: any): any {
  if (value === null || value === undefined) {
    return value;
  }

  // Handle date range objects
  if (typeof value === 'object' && 'start' in value && 'end' in value) {
    const result: any = {
      start: value.start
        ? (typeof value.start === 'string' && !DateConstantsService.isDateConstant(value.start)
          ? new Date(value.start)
          : value.start) // Keep date constants as strings
        : null,
      end: value.end
        ? (typeof value.end === 'string' && !DateConstantsService.isDateConstant(value.end)
          ? new Date(value.end)
          : value.end) // Keep date constants as strings
        : null
    };
    // Preserve other properties
    if (value.startTime !== undefined) result.startTime = value.startTime;
    if (value.endTime !== undefined) result.endTime = value.endTime;
    return result;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.map(item => deserializeFilterValue(item));
  }

  // Handle objects
  if (typeof value === 'object') {
    const deserialized: any = {};
    for (const [key, val] of Object.entries(value)) {
      deserialized[key] = deserializeFilterValue(val);
    }
    return deserialized;
  }

  return value;
}

/** Order type and payment channel filters: session-only, no localStorage, always "select all" on load */
function isSessionOnlyFilter(filterId: string, filters?: FilterConfig[]): boolean {
  if (!filterId) return false;
  const id = filterId.toLowerCase();
  const label = filters?.find(f => f.filterId === filterId)?.label?.toLowerCase() ?? '';
  return (
    id.includes('order-type') || id.includes('order_type') || label.includes('order type') ||
    id.includes('payment-channel') || id.includes('payment_channel') || label.includes('payment channel')
  );
}

/** Exclude session-only filters from persistence (they stay in memory only) */
function filterValuesForPersistence(values: Record<string, any>, filters?: FilterConfig[]): Record<string, any> {
  const keys = Object.keys(values).filter(k => !isSessionOnlyFilter(k, filters));
  const out: Record<string, any> = {};
  keys.forEach(k => { out[k] = values[k]; });
  return out;
}

function normalizeStringObjects(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle String objects - convert to primitive string
  if (typeof obj === 'object' && obj.constructor === String) {
    return obj.valueOf();
  }

  // Handle objects that look like String objects (character arrays)
  if (typeof obj === 'object' && !Array.isArray(obj)) {
    const keys = Object.keys(obj);
    const isStringObject = keys.length > 0 && keys.every((key, index) => {
      return key === index.toString() && typeof obj[key] === 'string' && obj[key].length === 1;
    });

    if (isStringObject) {
      // Convert character array back to string
      return keys.map(key => obj[key]).join('');
    }
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => normalizeStringObjects(item));
  }

  // Handle regular objects (but skip date objects and other legitimate objects)
  if (typeof obj === 'object') {
    // Skip date range objects (they have start/end properties)
    if ('start' in obj && 'end' in obj) {
      return obj; // Don't normalize date range objects
    }

    // Skip objects that look like legitimate configuration objects with meaningful keys
    const keys = Object.keys(obj);
    const hasOnlyNumericKeys = keys.every(key => /^\d+$/.test(key));

    if (!hasOnlyNumericKeys) {
      // This has meaningful string keys, process recursively but carefully
      const normalized: any = {};
      for (const [key, value] of Object.entries(obj)) {
        normalized[key] = normalizeStringObjects(value);
      }
      return normalized;
    }

    // Only normalize objects that actually look like character arrays (numeric keys only)
    const normalized: any = {};
    for (const [key, value] of Object.entries(obj)) {
      normalized[key] = normalizeStringObjects(value);
    }
    return normalized;
  }

  // Return primitive values as-is
  return obj;
}

// Styled components
const DashboardContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  position: 'relative', // Allow absolute positioning for overlay
  width: '100%',
  maxWidth: 'calc(100vw - 66px)', // Subtract sidebar width (50px)
  boxSizing: 'border-box',
}));

const DashboardHeader = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar - 1,
  backgroundColor: theme.palette.background.default,
  paddingBottom: theme.spacing(1),
}));

const ContentArea = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  position: 'relative',
}));

const TabPanel = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(3),
}));

const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.grey[900],
  color: theme.palette.common.white,
  borderRadius: theme.spacing(1),
}));

const VisualizationGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 400,
}));



const SettingsPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.palette.grey[900],
    color: theme.palette.common.white,
    minWidth: 280,
    maxWidth: 320,
  },
}));

const SettingsHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const SettingsContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
}));

const DataSourceItem = styled(ListItem)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanelComponent(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={true} timeout={300}>
          <TabPanel>{children}</TabPanel>
        </Fade>
      )}
    </div>
  );
}

interface TabbedAnalyticsDashboardProps extends AnalyticsDashboardProps {
  config: AnalyticsConfig;
  enableLegacyFallback?: boolean; // Option to enable config-driven API fallback
  dashboardKey?: string; // The dashboard.key for application routing
  showHeader?: boolean; // Option to hide the dashboard title and settings controls (defaults to true)
  initialUseLiveData?: boolean; // Start with live data instead of mock data (overrides config.dataSource)
  onFilterValuesChange?: (filterValues: Record<string, any>) => void; // Callback when filter values change
  onOpenFilterDialog?: (callback: () => void) => void; // Callback to register function to open filter dialog
  renderAdditionalCard?: () => React.ReactNode; // Optional additional card to render in the performer cards row
  onConfigUpdate?: (updatedConfig: AnalyticsConfig) => void; // Callback when config is auto-enhanced with layout values
}

export const TabbedAnalyticsDashboard: React.FC<TabbedAnalyticsDashboardProps> = ({
  config,
  defaultTab = config.tabs?.[0]?.id,
  onTabChange,
  className,
  enableLegacyFallback = false,
  dashboardKey,
  showHeader = true,
  initialUseLiveData,
  onFilterValuesChange,
  onOpenFilterDialog,
  renderAdditionalCard,
  onConfigUpdate
}) => {
  // Initialize activeTabIndex from defaultTab if provided, otherwise use 0
  const [activeTabIndex, setActiveTabIndex] = useState(() => {
    if (defaultTab && config.tabs) {
      const index = config.tabs.findIndex(tab => tab.id === defaultTab);
      return index >= 0 ? index : 0;
    }
    return 0;
  });
  const [activeSubTabIndex, setActiveSubTabIndex] = useState(0);
  // Property editor state for preview mode
  const [propertyEditorOpen, setPropertyEditorOpen] = useState(false);
  const [selectedVisualization, setSelectedVisualization] = useState<AnalyticsVisualization | null>(null);
  // Initialize filter values with defaults immediately to prevent timing issues
  // Cache for unfiltered hierarchical filter options (to prevent options from disappearing)
  const [hierarchicalFilterOptionsCache, setHierarchicalFilterOptionsCache] = useState<Record<string, any[]>>({});

  // Dynamic filters with options populated from actual data
  const [dynamicFilters, setDynamicFilters] = useState<any[]>(config.filters || []);

  // Track whether we've validated filter values for the current data source
  const filterValuesValidatedRef = useRef<boolean>(false);

  // Track if filter generation is currently in progress (prevents concurrent runs)
  const filterGenerationInProgressRef = useRef<boolean>(false);

  // 🔒 MASTER LOADING LOCK: Prevents ALL downstream processing until initial data load is complete
  // This ensures streaming data is fully received before triggering any filter generation or state updates
  const masterDataLoadingRef = useRef<boolean>(false);
  // Use state (not ref) so it triggers re-renders when loading completes
  const [masterDataLoadComplete, setMasterDataLoadComplete] = useState<boolean>(false);

  // Snackbar state for filter reset notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Shared state for payment channel detail toggles (synced between table and chart)
  const [showCreditCardDetail, setShowCreditCardDetail] = useState<boolean>(false);
  const [showThirdPartyDetail, setShowThirdPartyDetail] = useState<boolean>(false);
  const [showAllPaymentDetails, setShowAllPaymentDetails] = useState<boolean>(false); // Simplified=false, Detailed=true

  // Defer heavy expand/collapse updates to next frame to prevent UI freeze
  const handleCreditCardDetailChange = useCallback((value: boolean) => {
    requestAnimationFrame(() => setShowCreditCardDetail(value));
  }, []);
  const handleThirdPartyDetailChange = useCallback((value: boolean) => {
    requestAnimationFrame(() => setShowThirdPartyDetail(value));
  }, []);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('warning');

  // Multi-tab sync and conflict detection state
  const [localVersion, setLocalVersion] = useState(0);
  const [localTimestamp, setLocalTimestamp] = useState(Date.now());
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictData, setConflictData] = useState<{
    remote: Record<string, any>,
    local: Record<string, any>,
    remoteVersion: number,
    remoteTimestamp: number
  } | null>(null);

  // Generate a unique ID for this tab session
  const tabId = useRef(`tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  // Theme for consistent styling
  const theme = useTheme();

  // Chart-level filter state (separate from global dashboard filters)
  const [chartFilterValues, setChartFilterValues] = useState<Record<string, Record<string, any>>>(() => {
    const initialChartFilters: Record<string, Record<string, any>> = {};

    // Initialize chart filter values with defaults from visualization config
    config.tabs?.forEach(tab => {
      [...(tab.visualizations || []), ...(tab.subTabs?.flatMap(subTab => subTab.visualizations || []) || [])]
        .filter(viz => viz.type === 'chart' && (viz as any).config?.filters)
        .forEach(viz => {
          const chartFilters: Record<string, any> = {};
          (viz as any).config?.filters?.forEach((filter: any) => {
            if (filter.defaultValue !== undefined) {
              chartFilters[filter.id] = filter.defaultValue;
            }
          });
          if (Object.keys(chartFilters).length > 0) {
            initialChartFilters[viz.id] = chartFilters;
          }
        });
    });

    return initialChartFilters;
  });

  // Variance highlighting state - persisted across tabs using localStorage
  const STORAGE_KEY_HIGHLIGHT = 'mlr_highlight_deviations';
  const STORAGE_KEY_VARIANCE_TYPE = 'mlr_variance_type';
  const STORAGE_KEY_VARIANCE_DISPLAY = 'mlr_variance_display_mode';

  const [highlightDeviations, setHighlightDeviations] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_HIGHLIGHT);
      return saved === 'true';
    } catch {
      return false;
    }
  });

  const [varianceType, setVarianceType] = useState<'YoY' | 'MoM' | 'WoW'>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_VARIANCE_TYPE);
      return (saved === 'YoY' || saved === 'MoM' || saved === 'WoW') ? saved : 'YoY';
    } catch {
      return 'YoY';
    }
  });

  const [varianceDisplayMode, setVarianceDisplayMode] = useState<'absolute' | 'percent'>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_VARIANCE_DISPLAY);
      return (saved === 'absolute' || saved === 'percent') ? saved : 'absolute';
    } catch {
      return 'absolute';
    }
  });

  // Loading state for variance recalculation (like isBackendDataLoading pattern)
  const [isVarianceRecalculating, setIsVarianceRecalculating] = useState(false);
  const varianceRecalcTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Clear loading after calculations complete
  // NOTE: This effect should NOT run when varianceType/varianceDisplayMode changes
  // It should only run when isVarianceRecalculating changes
  React.useEffect(() => {
    if (isVarianceRecalculating && highlightDeviations) {
      console.log('🔄 [Variance Loading] useEffect triggered - isVarianceRecalculating is true, setting timeout to clear');
      // Clear any existing timeout
      if (varianceRecalcTimeoutRef.current) {
        clearTimeout(varianceRecalcTimeoutRef.current);
      }

      // Don't clear immediately - wait for table to render with loading state first
      // Use multiple requestAnimationFrames to ensure React has rendered AnalyticsTable
      const rafId1 = requestAnimationFrame(() => {
        const rafId2 = requestAnimationFrame(() => {
          console.log('🔄 [Variance Loading] Double RAF callback - setting timeout to clear loading');
          // Give more time for the UI to update and show the overlay
          varianceRecalcTimeoutRef.current = setTimeout(() => {
            console.log('🔄 [Variance Loading] Timeout fired - clearing loading state');
            setIsVarianceRecalculating(false);
          }, 800); // Increased to 800ms to ensure overlay has time to render
        });

        return () => cancelAnimationFrame(rafId2);
      });

      return () => {
        console.log('🔄 [Variance Loading] Cleaning up timeout');
        cancelAnimationFrame(rafId1);
        if (varianceRecalcTimeoutRef.current) {
          clearTimeout(varianceRecalcTimeoutRef.current);
        }
      };
    } else if (!isVarianceRecalculating) {
      console.log('🔄 [Variance Loading] isVarianceRecalculating is false - not setting timeout');
    }
  }, [isVarianceRecalculating, highlightDeviations]); // Removed varianceType/varianceDisplayMode from deps

  // Wrapper functions that set loading IMMEDIATELY (like isBackendDataLoading pattern)
  const handleVarianceTypeChange = React.useCallback((value: 'YoY' | 'MoM' | 'WoW') => {
    if (highlightDeviations && value !== varianceType) {
      console.log('🔄 [Variance Loading] handleVarianceTypeChange called - setting loading to true', { value, current: varianceType });
      // Set loading IMMEDIATELY and synchronously (like setIsBackendDataLoading)
      setIsVarianceRecalculating(true);
      console.log('🔄 [Variance Loading] isVarianceRecalculating set to true');
    }
    // Update state immediately
    setVarianceType(value);
  }, [highlightDeviations, varianceType]);

  const handleVarianceDisplayModeChange = React.useCallback((value: 'absolute' | 'percent') => {
    if (highlightDeviations && value !== varianceDisplayMode) {
      console.log('🔄 [Variance Loading] handleVarianceDisplayModeChange called - setting loading to true', { value, current: varianceDisplayMode });
      // Set loading IMMEDIATELY and synchronously (like setIsBackendDataLoading)
      setIsVarianceRecalculating(true);
      console.log('🔄 [Variance Loading] isVarianceRecalculating set to true');
    }
    // Update state immediately
    setVarianceDisplayMode(value);
  }, [highlightDeviations, varianceDisplayMode]);

  // Persist variance settings to localStorage when they change
  React.useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_HIGHLIGHT, String(highlightDeviations));
    } catch (error) {
      console.warn('[TabbedAnalyticsDashboard] Failed to persist highlightDeviations:', error);
    }
  }, [highlightDeviations]);

  React.useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_VARIANCE_TYPE, varianceType);
    } catch (error) {
      console.warn('[TabbedAnalyticsDashboard] Failed to persist varianceType:', error);
    }
  }, [varianceType]);

  React.useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_VARIANCE_DISPLAY, varianceDisplayMode);
    } catch (error) {
      console.warn('[TabbedAnalyticsDashboard] Failed to persist varianceDisplayMode:', error);
    }
  }, [varianceDisplayMode]);

  const [filterValues, setFilterValues] = useState<Record<string, any>>(() => {
    // Build default values from config
    const defaultValues: Record<string, any> = {};

    const allFilters = config.filters || [];

    // Add default values from global config filters
    config.filters?.forEach(filter => {
      if (isSessionOnlyFilter(filter.filterId, allFilters)) {
        defaultValues[filter.filterId] = filter.options?.map(o => o.value) ?? [];
      } else if (filter.defaultValue !== undefined) {
        const normalizedValue = normalizeStringObjects(filter.defaultValue);
        defaultValues[filter.filterId] = normalizedValue;
      }
    });

    // Add default values from filter visualizations in all tabs
    config.tabs?.forEach(tab => {
      tab.visualizations?.forEach(viz => {
        if (viz.type === 'filter' || viz.type === 'date-range-filter') {
          const filterConfig = viz.config as any;
          if (filterConfig?.filterId) {
            if (isSessionOnlyFilter(filterConfig.filterId, allFilters)) {
              defaultValues[filterConfig.filterId] = filterConfig.options?.map((o: any) => o.value) ?? [];
            } else if (filterConfig?.defaultValue !== undefined) {
              defaultValues[filterConfig.filterId] = normalizeStringObjects(filterConfig.defaultValue);
            }
          }
        }
      });
      tab.subTabs?.forEach(subTab => {
        subTab.visualizations?.forEach(viz => {
          if (viz.type === 'filter' || viz.type === 'date-range-filter') {
            const filterConfig = viz.config as any;
            if (filterConfig?.filterId) {
              if (isSessionOnlyFilter(filterConfig.filterId, allFilters)) {
                defaultValues[filterConfig.filterId] = filterConfig.options?.map((o: any) => o.value) ?? [];
              } else if (filterConfig?.defaultValue !== undefined) {
                defaultValues[filterConfig.filterId] = normalizeStringObjects(filterConfig.defaultValue);
              }
            }
          }
        });
      });
    });

    // Try to load saved filter values from localStorage
    // CRITICAL: Use mode-specific storage key to prevent filter state collisions between preview (editor) and live (reports) modes
    if (dashboardKey) {
      // Check if we're in preview mode (design mode in admin portal)
      const isPreviewMode = (config as any)._previewMode === true;
      const modeSuffix = isPreviewMode ? ':preview' : ':live';
      const storageKey = `dashboard:filters:${dashboardKey}${modeSuffix}`;
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Handle both old format (direct values) and new format (FilterState with metadata)
          const filterState: FilterState = parsed.version !== undefined
            ? parsed
            : { values: parsed, version: 0, timestamp: Date.now(), tabId: '' };

          // Deserialize filter values (especially dates)
          const deserialized = deserializeFilterValue(filterState.values);

          // Merge saved values with defaults, but handle multi-select filters specially
          // For arrays (multi-select filters), use saved value if it exists (even if empty)
          // Empty arrays represent explicit "no selection" state and should be preserved
          const result: Record<string, any> = { ...defaultValues };

          Object.keys(deserialized).forEach(filterId => {
            if (isSessionOnlyFilter(filterId, config.filters)) return; // Never restore from storage
            const savedValue = deserialized[filterId];
            if (filterId in deserialized) {
              if (Array.isArray(savedValue)) {
                result[filterId] = savedValue;
              } else {
                result[filterId] = savedValue !== undefined && savedValue !== null
                  ? savedValue
                  : defaultValues[filterId];
              }
            }
          });

          return result;
        }
      } catch (e) {
        console.warn(`[TabbedAnalyticsDashboard] Failed to load saved filters for ${dashboardKey}:`, e);
      }
    }

    return defaultValues;
  });
  const [filterValuesInitialized, setFilterValuesInitialized] = useState(true);

  // Filter history for undo/redo and recent filters
  const {
    recentFilters,
    addFilterState,
    canUndo,
    canRedo,
    undo,
    redo,
    applyRecentFilter,
    clearHistory,
    deleteFilters
  } = useFilterHistory(filterValues, config, 10);

  const [tabData, setTabData] = useState<Record<string, TabDataResponse>>({});

  // Track loading status per individual visualization for sequential skeleton loading
  // Format: Record<tabId, Record<visualizationId, boolean>>
  /** Get strictly necessary fields for a visualization to optimize data loading */
  const getRequiredFields = (visualization: AnalyticsVisualization): string[] => {
    const fields = new Set<string>(['date']); // Always need date for time grouping
    const config = visualization.config as any;

    if (!config) return Array.from(fields);

    if (visualization.type === 'kpi' || visualization.type === 'performance-card') {
      if (config.metric) fields.add(config.metric);
      if (config.formula) {
        // Extract field names from formula (simple regex for alphanumeric + underscore)
        const matches = config.formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g);
        matches?.forEach((f: string) => fields.add(f));
      }
    } else if (visualization.type === 'chart') {
      if (config.metrics) config.metrics.forEach((m: string) => fields.add(m));
      if (config.dimensions) config.dimensions.forEach((d: string) => fields.add(d));
      if (config.xAxis) fields.add(config.xAxis);
    } else if (visualization.type === 'table') {
      if (config.columns) config.columns.forEach((c: any) => fields.add(c.field));
    }

    return Array.from(fields);
  };

  const [loadingVisualizations, setLoadingVisualizations] = useState<Record<string, Record<string, boolean>>>({});

  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Individual visualization loading states for selective refresh
  const [visualizationLoading, setVisualizationLoading] = useState<Record<string, boolean>>({});
  const [visualizationErrors, setVisualizationErrors] = useState<Record<string, string>>({});

  // Global loading state for blocking UI during backend data loading
  const [isBackendDataLoading, setIsBackendDataLoading] = useState(false);


  // Settings state - determine initial data source based on props and config
  const [useMockData, setUseMockData] = useState(() => {
    // 1. Explicit prop override takes highest priority
    if (initialUseLiveData !== undefined) {
      return !initialUseLiveData;
    }

    // 2. Check config.dataSource - use live data for 'server' or 'hybrid'
    if (config.dataSource === 'server' || config.dataSource === 'hybrid') {
      return false; // Use live data
    }

    // 3. Default to mock data for 'mock' or 'datasets'
    return true;
  });
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLButtonElement | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [showFullValidationPanel, setShowFullValidationPanel] = useState(false);

  // Data export state
  const [exportFormat, setExportFormat] = useState<'csv' | 'json' | 'xlsx'>('csv');
  const [isExporting, setIsExporting] = useState(false);
  const [exportMode, setExportMode] = useState<'tab' | 'dataset' | 'data-only'>('tab');
  const [selectedDataset, setSelectedDataset] = useState<string>(''); // For dataset export

  // Configuration validation
  const {
    validationResult,
    isValidating,
    validate,
    hasErrors,
    hasWarnings,
    errorCount,
    warningCount,
    formatResults
  } = useConfigValidation(config, { validateOnChange: false });

  // Ref to track if component is mounted
  const isMountedRef = React.useRef(true);

  // Ref to prevent filter change loops
  const preventFilterLoopsRef = React.useRef(false);

  // Ref to track if initial data load has started (for pre-loading in end-user mode)
  const initialDataLoadStartedRef = React.useRef(false);

  // Ref to track which tabs have had their data loaded
  const loadedTabsRef = React.useRef<Set<string>>(new Set());

  // Ref to track if we just used preloaded data (to prevent redundant refresh)
  const justUsedPreloadedDataRef = React.useRef(false);

  // Find active tab and sub-tab
  // Track if we've already enhanced this config version to prevent infinite loops
  const configEnhancementRef = React.useRef<string>('');

  // Auto-enhance config with layout values if missing (only once per config change)
  React.useEffect(() => {
    if (!onConfigUpdate || !config.tabs) return;

    // Create a hash of the config to detect if it's changed
    const configHash = JSON.stringify(config.tabs?.map(tab => ({
      id: tab.id,
      visualizations: tab.visualizations?.map((v: any) => ({ id: v.id, layout: v.layout })),
      subTabs: tab.subTabs?.map((st: any) => ({
        id: st.id,
        visualizations: st.visualizations?.map((v: any) => ({ id: v.id, layout: v.layout }))
      }))
    })));

    // Skip if we've already enhanced this version
    if (configEnhancementRef.current === configHash) return;

    const cardTypes = ['kpi', 'performance-card', 'ai-insight'];
    let hasChanges = false;
    const updatedTabs = config.tabs.map(tab => {
      const allVisualizations = [
        ...(tab.visualizations || []),
        ...(tab.subTabs?.flatMap(subTab => subTab.visualizations || []) || [])
      ];

      // Check if any visualizations need layout enhancement
      const needsEnhancement = allVisualizations.some(viz => {
        const hasOrder = viz.layout?.order !== undefined && viz.layout?.order !== null;
        const hasSpan = viz.layout?.span !== undefined;
        return !hasOrder || !hasSpan;
      });

      if (!needsEnhancement) return tab;

      hasChanges = true;

      // Enhance main tab visualizations
      const enhancedVisualizations = (tab.visualizations || []).map((viz, idx, arr) => {
        const hasOrder = viz.layout?.order !== undefined && viz.layout?.order !== null;
        const hasSpan = viz.layout?.span !== undefined;
        if (hasOrder && hasSpan) return viz;

        // Count cards/charts/tables before this one
        const cardsBefore = arr.slice(0, idx).filter(v =>
          cardTypes.includes(v.type) &&
          (v.layout?.order === undefined || v.layout?.order === null)
        ).length;
        const chartsBefore = arr.slice(0, idx).filter(v =>
          v.type === 'chart' && (v.layout?.order === undefined || v.layout?.order === null)
        ).length;
        const tablesBefore = arr.slice(0, idx).filter(v =>
          v.type === 'table' && (v.layout?.order === undefined || v.layout?.order === null)
        ).length;

        const totalCards = arr.filter(v =>
          cardTypes.includes(v.type) && (v.layout?.order === undefined || v.layout?.order === null)
        ).length;
        const totalCharts = arr.filter(v =>
          v.type === 'chart' && (v.layout?.order === undefined || v.layout?.order === null)
        ).length;
        const totalTables = arr.filter(v =>
          v.type === 'table' && (v.layout?.order === undefined || v.layout?.order === null)
        ).length;

        let inferredOrder: number;
        let inferredSpan: number;

        if (cardTypes.includes(viz.type)) {
          inferredSpan = 3;
          inferredOrder = cardsBefore + 1;
        } else if (viz.type === 'chart') {
          inferredSpan = 12;
          inferredOrder = totalCards + chartsBefore + 1;
        } else if (viz.type === 'table') {
          inferredSpan = 12;
          inferredOrder = totalCards + totalCharts + tablesBefore + 1;
        } else {
          const othersBefore = arr.slice(0, idx).filter(v =>
            !cardTypes.includes(v.type) && v.type !== 'chart' && v.type !== 'table' &&
            (v.layout?.order === undefined || v.layout?.order === null)
          ).length;
          inferredSpan = 12;
          inferredOrder = totalCards + totalCharts + totalTables + othersBefore + 1;
        }

        return {
          ...viz,
          layout: {
            ...viz.layout,
            order: viz.layout?.order ?? inferredOrder,
            span: viz.layout?.span ?? inferredSpan,
          }
        };
      });

      // Enhance sub-tab visualizations
      const enhancedSubTabs = tab.subTabs?.map(subTab => {
        const subVisualizations = (subTab.visualizations || []).map((viz, idx, arr) => {
          const hasOrder = viz.layout?.order !== undefined && viz.layout?.order !== null;
          const hasSpan = viz.layout?.span !== undefined;
          if (hasOrder && hasSpan) return viz;

          const cardsBefore = arr.slice(0, idx).filter(v =>
            cardTypes.includes(v.type) && (v.layout?.order === undefined || v.layout?.order === null)
          ).length;
          const chartsBefore = arr.slice(0, idx).filter(v =>
            v.type === 'chart' && (v.layout?.order === undefined || v.layout?.order === null)
          ).length;
          const tablesBefore = arr.slice(0, idx).filter(v =>
            v.type === 'table' && (v.layout?.order === undefined || v.layout?.order === null)
          ).length;

          const totalCards = arr.filter(v =>
            cardTypes.includes(v.type) && (v.layout?.order === undefined || v.layout?.order === null)
          ).length;
          const totalCharts = arr.filter(v =>
            v.type === 'chart' && (v.layout?.order === undefined || v.layout?.order === null)
          ).length;
          const totalTables = arr.filter(v =>
            v.type === 'table' && (v.layout?.order === undefined || v.layout?.order === null)
          ).length;

          let inferredOrder: number;
          let inferredSpan: number;

          if (cardTypes.includes(viz.type)) {
            inferredSpan = 3;
            inferredOrder = cardsBefore + 1;
          } else if (viz.type === 'chart') {
            inferredSpan = 12;
            inferredOrder = totalCards + chartsBefore + 1;
          } else if (viz.type === 'table') {
            inferredSpan = 12;
            inferredOrder = totalCards + totalCharts + tablesBefore + 1;
          } else {
            const othersBefore = arr.slice(0, idx).filter(v =>
              !cardTypes.includes(v.type) && v.type !== 'chart' && v.type !== 'table' &&
              (v.layout?.order === undefined || v.layout?.order === null)
            ).length;
            inferredSpan = 12;
            inferredOrder = totalCards + totalCharts + totalTables + othersBefore + 1;
          }

          return {
            ...viz,
            layout: {
              ...viz.layout,
              order: viz.layout?.order ?? inferredOrder,
              span: viz.layout?.span ?? inferredSpan,
            }
          };
        });

        return {
          ...subTab,
          visualizations: subVisualizations
        };
      });

      return {
        ...tab,
        visualizations: enhancedVisualizations,
        ...(enhancedSubTabs && { subTabs: enhancedSubTabs })
      };
    });

    if (hasChanges && onConfigUpdate) {
      const updatedConfig = {
        ...config,
        tabs: updatedTabs
      };
      // Mark this config version as enhanced
      configEnhancementRef.current = configHash;
      onConfigUpdate(updatedConfig);
    } else {
      // Even if no changes, mark as processed to prevent re-checking
      configEnhancementRef.current = configHash;
    }
  }, [config, onConfigUpdate]); // Only run when config changes

  const activeTab = config.tabs?.[activeTabIndex];
  const activeSubTab = activeTab?.subTabs?.[activeSubTabIndex];
  const currentTab = activeSubTab || activeTab;

  // Analytics hook for data fetching
  const { service } = useAnalyticsService({ useMockData, dashboardConfig: config, enableLegacyFallback, dashboardKey });

  // Smart data manager for intelligent client/server operations
  const [smartDataManager, setSmartDataManager] = useState<SmartDataManager | null>(null);

  // Get API configuration with dashboard config and key
  const { baseUrl, appKey, getAuthToken } = useApiConfig({ dashboardConfig: config, dashboardKey });

  // Initialize smart data manager with appropriate service based on useMockData state
  useEffect(() => {
    let serviceInstance;


    if (useMockData) {
      serviceInstance = new PlaceholderAnalyticsService();
    } else {
      serviceInstance = new BackendAnalyticsService(baseUrl, appKey, getAuthToken, enableLegacyFallback);
      // Set dashboard configuration for dynamic endpoint building
      serviceInstance.setDashboardConfig(config);
    }

    const manager = new SmartDataManager(serviceInstance, { baseUrl, appKey, dashboardConfig: config, getAuthToken });

    // Set mock data preference BEFORE registering dashboard config
    // This ensures DataSetManager knows whether to use mock or server data
    manager.setUseMockData(useMockData);

    // Register dashboard config to create virtual datasets when needed
    if (config.dataSource === 'mock' && config.mockDataConfig) {
      // 🚀 Register dashboard configuration for unified data (both mock and live)
      // This creates virtual datasets that work for both mock and server modes
      manager.registerDashboardConfig(config);
    } else if (config.dataSource === 'datasets' && config.dataSets) {
      // If using static datasets, still register to pre-cache static datasets
      manager.registerDashboardConfig(config);
    } else if (config.dataSource === 'server' || config.dataSource === 'hybrid') {
      // Register dataset for server/hybrid mode so filter generation can work
      // Even without mockDataConfig, we need to register the dataset for dynamic filter option generation
      manager.registerDashboardConfig(config);
    }


    // ✅ Check if DataSet framework should be enabled
    const hasExplicitDataSets = config.dataSets && Object.keys(config.dataSets).length > 0;
    const hasMockDataConfig = config.mockDataConfig && config.dataSource === 'mock'; // DataSet framework available regardless of useMockData toggle
    const shouldUseDataSetFramework = hasExplicitDataSets || hasMockDataConfig;



    // 🚀 Initialize Data Set Framework if available
    if (shouldUseDataSetFramework) {
      if (hasExplicitDataSets) {
        manager.initializeDataSets(config.dataSets, activeTab?.id);
      }
      // For mock data dashboards, registerDashboardConfig() already set up the virtual datasets
      // No need to call initializeDataSets() again as it would override the virtual dataset context
    }

    // Set initial currentTabId
    const initialTab = config.tabs?.[activeTabIndex];
    if (initialTab) {
      (manager as any).currentTabId = initialTab.id;
    }

    setSmartDataManager(manager);
  }, [useMockData, baseUrl, appKey, getAuthToken, enableLegacyFallback, dashboardKey, config.id, config.dataSource]); // Include all stable dependencies

  // 🎯 SMART PRE-LOAD: Check cache first, then fetch if needed
  // This effect runs when smartDataManager is ready and handles both cached and fresh data
  // 🔒 ATOMIC LOADING: Sets master lock, loads ALL data, then releases lock for downstream processing
  useEffect(() => {
    // Only in live data mode (end-user mode) when not using mock data
    if (!useMockData && smartDataManager && activeTab && filterValuesInitialized && dashboardKey) {
      const tabId = activeTab.id;

      // Skip if we've already loaded this tab or master load is complete
      if (initialDataLoadStartedRef.current || loadedTabsRef.current.has(tabId)) {
        return;
      }

      // Skip if master loading is in progress (another load is already running)
      if (masterDataLoadingRef.current) {
        console.log('🔒 [Smart Load] Master loading in progress, skipping duplicate load');
        return;
      }

      // 🔒 ACQUIRE MASTER LOCK - prevents filter generation and other effects from running
      masterDataLoadingRef.current = true;
      setMasterDataLoadComplete(false);
      console.log('🔒 [Smart Load] Master loading lock ACQUIRED');

      // 🚫 PRELOADER DISABLED FOR DEBUGGING
      // const preloadedData = analyticsPreloader.getPreloadedData(dashboardKey);

      // 📥 Always load data fresh (preloader disabled)
      initialDataLoadStartedRef.current = true;
      console.log('📥 [Smart Load] Loading data fresh (preloader disabled)');

      // Start loading data - this will stream ALL data before returning
      loadTabData(activeTab).then(() => {
        loadedTabsRef.current.add(tabId);

        // 🔓 RELEASE MASTER LOCK - streaming is complete, all data is ready
        masterDataLoadingRef.current = false;
        setMasterDataLoadComplete(true);
        console.log('🔓 [Smart Load] Master loading lock RELEASED (from API)');
      }).catch((error) => {
        console.error('❌ [Smart Load] Data fetch failed for tab:', tabId, error);
        // Release lock even on error to prevent deadlock
        masterDataLoadingRef.current = false;
        setMasterDataLoadComplete(true);
      });
    }
  }, [smartDataManager, activeTab, activeSubTabIndex, filterValuesInitialized, useMockData, dashboardKey]);

  // Generate dynamic filter options from actual data when switching to live data
  // 🔒 This effect WAITS for master data load to complete before running
  useEffect(() => {
    const generateFilterOptions = async () => {
      if (!smartDataManager || useMockData || !config.filters || config.filters.length === 0) {
        // Reset to static filters from config when using mock data
        if (useMockData) {
          setDynamicFilters(config.filters || []);
          filterValuesValidatedRef.current = false; // Reset validation flag
        }
        return;
      }

      // Skip if we've already validated for this data source
      if (filterValuesValidatedRef.current) {
        return;
      }

      // Skip if filter generation is already in progress (prevents concurrent runs)
      if (filterGenerationInProgressRef.current) {
        return;
      }

      // 🔒 WAIT FOR MASTER LOAD: Don't start filter generation until initial data load is complete
      // This ensures we have ALL streamed data before building filter options
      if (masterDataLoadingRef.current) {
        console.log('🔒 [Filter Gen] Waiting for master data load to complete...');
        return; // Effect will re-run when masterDataLoadComplete state changes
      }

      // If we're in live mode but master load hasn't completed yet, wait for it
      if (!useMockData && dashboardKey && !masterDataLoadComplete) {
        console.log('🔒 [Filter Gen] Master load not complete yet, waiting...');
        return;
      }

      // Mark as in progress
      filterGenerationInProgressRef.current = true;
      console.log('🚀 [Filter Gen] Starting filter generation (master load complete)');
      console.log('🚀 [Filter Gen] Current filterValues:', filterValues);

      try {
        // Track which filters need their values updated
        const filterValueUpdates: Record<string, any> = {};
        let hasInvalidSelections = false;

        // Generate dynamic options for each filter
        const updatedFilters = await Promise.all(
          config.filters.map(async (filter: any) => {
            // Skip date-range and number-range filters (they don't need options)
            if (filter.type === 'date-range' || filter.type === 'number-range') {
              return filter;
            }

            // Skip filters that don't have a field specified
            if (!filter.field) {
              console.warn(`⚠️ [Dynamic Filters] Filter ${filter.filterId} has no field property, skipping`);
              return filter;
            }

            // Generate options from the dataset
            const dataSetId = `${config.id}-dataset`; // Use dashboard dataset
            const options = await smartDataManager.generateDynamicFilterOptions(
              dataSetId,
              filter.field,
              filter.hierarchyConfig
            );

            // Validate current filter values against new options
            const currentValue = filterValues[filter.filterId];

            console.log(`🔍 [Filter Validation] Filter: ${filter.filterId}, currentValue:`, currentValue);
            console.log(`🔍 [Filter Validation] Options count: ${options.length}`);

            if (options.length > 0) {
              // For hierarchical filters (e.g. region → location), only treat leaf nodes as
              // selectable values. Parent/region nodes are structural and should never be
              // included in the stored selection or counted toward the location chip.
              const isHierarchicalFilter = filter.hierarchical === true || filter.hierarchyConfig?.levels?.length > 0;
              const validValues = new Set<string>();
              const extractValues = (opts: any[]) => {
                opts.forEach((opt: any) => {
                  if (opt.children && opt.children.length > 0) {
                    // Parent node (region) — descend but don't add to valid selections
                    if (!isHierarchicalFilter) {
                      validValues.add(opt.value);
                    }
                    extractValues(opt.children);
                  } else {
                    // Leaf node (location) — always valid
                    validValues.add(opt.value);
                  }
                });
              };
              extractValues(options);

              console.log(`🔍 [Filter Validation] Valid values count: ${validValues.size}, sample:`, Array.from(validValues).slice(0, 3));

              // Handle undefined, null, or empty current value - default to "Select All"
              // CRITICAL: In live mode, empty arrays mean "select all" - don't override with mock data values
              // Only set to "Select All" if we're in mock mode OR if filter was explicitly reset
              if (!currentValue || !Array.isArray(currentValue) || currentValue.length === 0) {
                // In live mode, empty array means "select all" - keep it empty to show all data
                // In mock mode, auto-select all options for better UX
                if (useMockData) {
                  console.log(`🔍 [Filter Validation] No current value in mock mode - setting to Select All`);
                  const allValues = Array.from(validValues);
                  filterValueUpdates[filter.filterId] = allValues;
                  hasInvalidSelections = true;
                } else {
                  // In live mode, empty array = select all, so keep it empty
                  console.log(`🔍 [Filter Validation] No current value in live mode - keeping empty (select all)`);
                  // Don't update - empty array is correct for "select all" in live mode
                }
              } else {
                // Strip any region/parent values that may have been persisted in localStorage
                // from a previous version of the bug, then check for truly invalid selections.
                const leafOnlyCurrentValue = isHierarchicalFilter
                  ? currentValue.filter((val: any) => validValues.has(val))
                  : currentValue;
                const invalidSelections = leafOnlyCurrentValue.filter((val: any) => !validValues.has(val));

                console.log(`🔍 [Filter Validation] Invalid selections: ${invalidSelections.length}`, invalidSelections.slice(0, 3));

                // If we stripped region values, persist the cleaned-up selection
                if (leafOnlyCurrentValue.length !== currentValue.length) {
                  console.log(`🔍 [Filter Validation] Removed ${currentValue.length - leafOnlyCurrentValue.length} region/parent values from ${filter.filterId}`);
                  filterValueUpdates[filter.filterId] = leafOnlyCurrentValue.length > 0 ? leafOnlyCurrentValue : Array.from(validValues);
                  hasInvalidSelections = true;
                } else if (invalidSelections.length > 0) {
                  console.log(`🔍 [Filter Validation] Resetting ${filter.filterId} to Select All due to invalid values`);
                  // Collect all valid leaf values for "Select All"
                  const allValues = Array.from(validValues);
                  filterValueUpdates[filter.filterId] = allValues;
                  hasInvalidSelections = true;
                }
              }
            } else if (currentValue && Array.isArray(currentValue) && currentValue.length > 0) {
              // No options generated but filter has values, clear the filter
              filterValueUpdates[filter.filterId] = [];
              hasInvalidSelections = true;
            }

            // Return filter with dynamic options
            return {
              ...filter,
              options: options.length > 0 ? options : filter.options // Fallback to static if no options generated
            };
          })
        );

        setDynamicFilters(updatedFilters);

        // Update filter values if any invalid selections were found
        if (hasInvalidSelections && Object.keys(filterValueUpdates).length > 0) {
          // CRITICAL: Reset BOTH refs BEFORE setFilterValues so the filter change effect
          // can trigger when React re-renders with the new filter values.
          // The order matters: setFilterValues triggers a re-render which fires the
          // filter change effect immediately, so these refs must be false before that.
          preventFilterLoopsRef.current = false;
          filterGenerationInProgressRef.current = false;
          filterValuesValidatedRef.current = true;

          console.log(`🔄 [Filter Validation] Resetting filters and triggering refresh`);
          console.log(`🔄 [Filter Validation] New filter values:`, filterValueUpdates);

          setFilterValues(prev => ({
            ...prev,
            ...filterValueUpdates
          }));

          // Build user-friendly message
          const updatedFilterNames = Object.keys(filterValueUpdates).map(id => {
            const filter = config.filters?.find((f: any) => f.filterId === id);
            return filter?.label || id;
          }).join(', ');

          // Show user notification
          const filterCount = Object.keys(filterValueUpdates).length;
          const message = filterCount === 1
            ? `"${updatedFilterNames}" filter reset to "Select All" to match live data`
            : `${filterCount} filters reset to "Select All" to match live data: ${updatedFilterNames}`;

          setSnackbarMessage(message);
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);

          // The filter change effect will handle the reload automatically when
          // filterValues updates. We reset the refs above to ensure it can run.
          return; // Early return since we've handled the refs
        }

        // Mark as validated to prevent re-running
        filterValuesValidatedRef.current = true;
        filterGenerationInProgressRef.current = false;

      } catch (error) {
        console.error('❌ [Dynamic Filters] Error generating filter options:', error);
        // Fallback to static filters on error
        setDynamicFilters(config.filters || []);
        filterGenerationInProgressRef.current = false;
      }
    };

    generateFilterOptions();
  }, [smartDataManager, useMockData, config.filters, config.id, masterDataLoadComplete, dashboardKey]);

  // Clear hierarchical filter options cache when data source changes
  useEffect(() => {
    if (smartDataManager) {
      setHierarchicalFilterOptionsCache({});

      // CRITICAL FIX: Also clear hierarchical filter VALUES to prevent mixing mock + live data
      setFilterValues(prev => {
        const updated = { ...prev };

        // Clear any hierarchical filter values (they contain old data from previous source)
        Object.keys(updated).forEach(key => {
          if (key.includes('hierarchy') || key.includes('location-hierarchy')) {
            delete updated[key];
          }
        });

        return updated;
      });

      // Clear tab data cache to force reload with new data source
      setTabData({});

      // Clear loaded tabs tracking when switching data sources
      loadedTabsRef.current.clear();
      initialDataLoadStartedRef.current = false;

      // Reset refresh state to allow new loads
      preventFilterLoopsRef.current = false;

      // Note: Don't call loadTabData here - let the dynamic filters effect handle it
      // after it finishes generating options and validating filter values.
      // Calling loadTabData here causes a race condition where data loads with
      // empty/invalid filter values before validation completes.
    }
  }, [useMockData, config.id, config.dataSource]); // Clear cache when data source changes

  // Auto-select first dataset when switching to dataset or data-only export mode
  React.useEffect(() => {
    if ((exportMode === 'dataset' || exportMode === 'data-only') && !selectedDataset && smartDataManager) {
      const availableDatasets = getAvailableDatasets();
      if (availableDatasets.length > 0) {
        setSelectedDataset(availableDatasets[0].id);
        debugLog(`🔧 [Export] Auto-selected first dataset for ${exportMode}: ${availableDatasets[0].id}`);
      }
    }
  }, [exportMode, smartDataManager]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
      preventFilterLoopsRef.current = false;
      initialDataLoadStartedRef.current = false;
      loadedTabsRef.current.clear();
    };
  }, []);



  // Filter values are now initialized immediately in useState, no need for useEffect

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTabIndex(newValue);
    setActiveSubTabIndex(0); // Reset sub-tab to first when main tab changes
    const newTab = config.tabs?.[newValue];

    // Reset initial load flag to allow pre-loading for new tab
    initialDataLoadStartedRef.current = false;

    // Update currentTabId in SmartDataManager for tab-specific filtering
    if (newTab && smartDataManager) {
      (smartDataManager as any).currentTabId = newTab.id;
    }

    if (newTab && onTabChange) {
      onTabChange(newTab.id);
    }
  };

  // Handle sub-tab change
  const handleSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveSubTabIndex(newValue);
  };

  // Handle filter changes for unified filters
  // Notify parent of filter value changes
  useEffect(() => {
    if (onFilterValuesChange) {
      onFilterValuesChange(filterValues);
    }
  }, [filterValues, onFilterValuesChange]);

  // Save filter values to localStorage for persistence with version metadata
  useEffect(() => {
    if (!dashboardKey || !filterValuesInitialized) return;

    // Check if we're in preview mode (design mode in admin portal)
    const isPreviewMode = (config as any)._previewMode === true;
    const modeSuffix = isPreviewMode ? ':preview' : ':live';
    const storageKey = `dashboard:filters:${dashboardKey}${modeSuffix}`;
    try {
      const toPersist = filterValuesForPersistence(filterValues, config.filters);
      const serialized = serializeFilterValue(toPersist);
      const newVersion = localVersion + 1;
      const newTimestamp = Date.now();

      const filterState: FilterState = {
        values: serialized,
        version: newVersion,
        timestamp: newTimestamp,
        tabId: tabId.current
      };

      localStorage.setItem(storageKey, JSON.stringify(filterState));
      setLocalVersion(newVersion);
      setLocalTimestamp(newTimestamp);
      setHasLocalChanges(false); // Reset after save
    } catch (e) {
      console.warn(`[TabbedAnalyticsDashboard] Failed to save filters for ${dashboardKey}${modeSuffix}:`, e);
    }
  }, [filterValues, dashboardKey, filterValuesInitialized, config]);

  // Sync filter values across browser tabs/windows with conflict detection
  useEffect(() => {
    if (!dashboardKey) return;

    // Check if we're in preview mode (design mode in admin portal)
    const isPreviewMode = (config as any)._previewMode === true;
    const modeSuffix = isPreviewMode ? ':preview' : ':live';
    const storageKey = `dashboard:filters:${dashboardKey}${modeSuffix}`;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          // Handle both old format and new FilterState format
          const remoteState: FilterState = parsed.version !== undefined
            ? parsed
            : { values: parsed, version: 0, timestamp: Date.now(), tabId: '' };

          // Skip if this change came from this tab (shouldn't happen with storage event, but just in case)
          if (remoteState.tabId === tabId.current) {
            return;
          }

          // CONFLICT DETECTION
          // Check if we have local changes that haven't been saved yet
          if (hasLocalChanges) {
            console.log('[Multi-tab Sync] Conflict detected! User has unsaved changes.');
            // We have a conflict! Show the dialog
            setConflictData({
              remote: remoteState.values,
              local: serializeFilterValue(filterValues),
              remoteVersion: remoteState.version,
              remoteTimestamp: remoteState.timestamp
            });
            setShowConflictDialog(true);
            return; // Don't auto-apply remote changes
          }

          // NO CONFLICT - check if remote is newer than our local state
          if (remoteState.version > localVersion ||
            remoteState.timestamp > localTimestamp) {

            const deserialized = deserializeFilterValue(remoteState.values);

            setFilterValues(prev => {
              const merged = { ...prev };
              Object.keys(deserialized).forEach(k => {
                if (!isSessionOnlyFilter(k, config.filters)) merged[k] = deserialized[k];
              });
              const prevStr = JSON.stringify(serializeFilterValue(filterValuesForPersistence(prev, config.filters)));
              const newStr = JSON.stringify(filterValuesForPersistence(merged, config.filters));
              if (prevStr !== newStr) {
                console.log('[Multi-tab Sync] Applying remote filter changes from another tab');
                setSnackbarMessage('Filters updated from another tab');
                setSnackbarSeverity('info');
                setSnackbarOpen(true);
                setLocalVersion(remoteState.version);
                setLocalTimestamp(remoteState.timestamp);
                return merged;
              }
              return prev;
            });
          }
        } catch (e) {
          console.warn(`[TabbedAnalyticsDashboard] Failed to parse filter update from storage:`, e);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [dashboardKey, hasLocalChanges, filterValues, localVersion, localTimestamp]);

  const handleFilterChange = (newFilterValues: Record<string, any>) => {
    setHasLocalChanges(true); // Mark that we have unsaved changes for conflict detection
    setFilterValues(prev => {
      const updated = { ...prev, ...newFilterValues };

      // Add to filter history for undo/redo
      addFilterState(updated);

      return updated;
    });
  };

  // Undo/Redo handlers with useCallback to prevent dependency issues
  const handleUndo = React.useCallback(() => {
    const prevFilters = undo();
    if (prevFilters) {
      setFilterValues(prevFilters);
      setHasLocalChanges(true);
    }
  }, [undo]);

  const handleRedo = React.useCallback(() => {
    const nextFilters = redo();
    if (nextFilters) {
      setFilterValues(nextFilters);
      setHasLocalChanges(true);
    }
  }, [redo]);

  const handleApplyRecentFilter = React.useCallback((filterId: string) => {
    const filters = applyRecentFilter(filterId);
    if (filters) {
      setFilterValues(filters);
      setHasLocalChanges(true);
    }
  }, [applyRecentFilter]);

  // Keyboard shortcuts for undo/redo - Must come AFTER function definitions
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl/Cmd + Z (undo) or Ctrl/Cmd + Shift + Z (redo)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        if (e.shiftKey) {
          // Redo: Ctrl+Shift+Z or Cmd+Shift+Z
          if (canRedo) {
            e.preventDefault();
            handleRedo();
          }
        } else {
          // Undo: Ctrl+Z or Cmd+Z
          if (canUndo) {
            e.preventDefault();
            handleUndo();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, handleUndo, handleRedo]);

  // Initialize filter without triggering refresh (for tab-specific filters)
  const initializeFilterSilently = (filterId: string, defaultValue: any) => {
    if (preventFilterLoopsRef.current) return; // Don't set filters during refresh to prevent loops

    setFilterValues(prev => {
      // Only set if not already present
      if (prev[filterId] === undefined) {
        return { ...prev, [filterId]: defaultValue };
      }
      return prev;
    });
  };

  // Get tab-specific filters for a given tab - SIMPLIFIED
  const getTabSpecificFilters = (tabId: string, visualizationId?: string): Record<string, any> => {
    // Return global filters - chart behavior is determined by config properties
    return filterValues;
  };


  // Refresh only data visualizations (selective refresh for filter changes)  
  const refreshDataVisualizationsOnly = async (tab: AnalyticsTab) => {
    console.log(`🔄 [refreshDataVisualizationsOnly] START - Tab: ${tab?.id}`);

    if (!tab || !smartDataManager) {
      console.log(`🔄 [refreshDataVisualizationsOnly] SKIP - no tab or smartDataManager`);
      return;
    }
    if (!isMountedRef.current) {
      console.log(`🔄 [refreshDataVisualizationsOnly] SKIP - component unmounted`);
      return;
    }

    // Clear source data cache at start of each refresh cycle
    (smartDataManager as any).sourceDataCache?.clear();

    // Show global loading overlay if using backend data
    if (!useMockData) {
      setIsBackendDataLoading(false);
    }

    try {
      // Collect all visualizations from main tab and sub-tabs
      const allVisualizations: any[] = [];

      if (tab.visualizations) {
        allVisualizations.push(...tab.visualizations);
      }

      // Add sub-tab visualizations with priority for the active sub-tab
      if (tab.subTabs && tab.subTabs.length > 0) {
        const activeSubTab = tab.subTabs[activeSubTabIndex] || tab.subTabs[0];

        if (activeSubTab.visualizations) {
          allVisualizations.push(...activeSubTab.visualizations);
        }
      }

      console.log(`🔄 [refreshDataVisualizationsOnly] Total visualizations: ${allVisualizations.length}`);

      // Filter to only data visualizations (exclude filters)
      const dataVisualizations = allVisualizations.filter(viz =>
        viz.dataQuery && viz.type !== 'date-range-filter' && viz.type !== 'filter'
      );

      console.log(`🔄 [refreshDataVisualizationsOnly] Data visualizations to refresh: ${dataVisualizations.length}`);

      if (dataVisualizations.length === 0) {
        console.log(`🔄 [refreshDataVisualizationsOnly] SKIP - no data visualizations`);
        return;
      }

      // 1. Mark all visualizations in this tab as loading for skeleton display
      setLoadingVisualizations(prev => {
        const next = { ...prev };
        const tabLoading: Record<string, boolean> = {};
        allVisualizations.forEach(viz => {
          tabLoading[viz.id] = true;
        });
        next[tab.id] = tabLoading;
        return next;
      });

      // UI Yield: Allow browser to paint the skeleton loaders before starting fetches
      await new Promise(resolve => setTimeout(resolve, 0));

      // 2. Start all fetches in parallel to minimize total IO time
      const fetchItems = dataVisualizations.map(visualization => {
        const isCalculatedKpi = visualization.type === 'kpi' &&
          (visualization.config?.mode === 'calculated' || visualization.config?.mode === 'formula');
        const isCalculatedPerformanceCard = visualization.type === 'performance-card' &&
          (visualization.config?.mode === 'calculated' || visualization.config?.mode === 'formula');
        const isCalculatedChart = visualization.type === 'chart' &&
          (visualization.config?.mode === 'calculated');

        const forceRefresh = visualization.type === 'table' || isCalculatedKpi || isCalculatedPerformanceCard || isCalculatedChart;
        const tabSpecificFilters = getTabSpecificFilters(tab.id);

        return {
          visualization,
          promise: smartDataManager.loadVisualizationData(
            visualization,
            tabSpecificFilters,
            forceRefresh,
            getRequiredFields(visualization)
          )
        };
      });

      // 3. Process results one by one in original order (top-to-bottom pop-in)
      for (const item of fetchItems) {
        if (!isMountedRef.current) break;
        const { visualization, promise } = item;

        try {
          const response = await promise;

          if (isMountedRef.current && response) {
            // Update this specific visualization in tabData immediately (incremental pop-in)
            setTabData(prev => {
              const currentTabData = prev[tab.id] || {
                configId: config.id,
                tabId: tab.id,
                data: [],
                metadata: {
                  totalRecords: 0,
                  processingTime: 0,
                  cacheHit: false,
                  dataSource: config.dataSource,
                  lastRefreshed: new Date()
                },
                visualizations: {}
              };

              return {
                ...prev,
                [tab.id]: {
                  ...(currentTabData as any),
                  visualizations: {
                    ...((currentTabData as any).visualizations || {}),
                    [visualization.id]: response
                  },
                  metadata: {
                    ...currentTabData.metadata,
                    lastRefreshed: new Date()
                  }
                }
              };
            });

            // Mark this specific visualization as loaded
            setLoadingVisualizations(prev => ({
              ...prev,
              [tab.id]: {
                ...(prev[tab.id] || {}),
                [visualization.id]: false
              }
            }));

            // 4. UI Yielding (Slicing): Micro-gap allows React to render and browser to remain responsive
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        } catch (error) {
          console.error(`Failed to load data for visualization ${visualization.id}:`, error);
          setLoadingVisualizations(prev => ({
            ...prev,
            [tab.id]: {
              ...(prev[tab.id] || {}),
              [visualization.id]: false
            }
          }));
        }
      }

    } catch (error) {
      console.error('Failed to refresh data visualizations:', error);
    } finally {
      // Hide global loading overlay if component is still mounted
      if (isMountedRef.current) {
        setIsBackendDataLoading(false);
      }
    }
  };



  // Load data for a specific tab (full refresh)
  const loadTabData = async (tab: AnalyticsTab) => {
    if (!tab || !smartDataManager) {
      return;
    }

    if (!isMountedRef.current) {
      return; // Early return if component is unmounted
    }

    // Clear source data cache at start of tab load to allow fresh sharing within this tab
    (smartDataManager as any).sourceDataCache?.clear();

    setLoading(prev => ({ ...prev, [tab.id]: true }));
    setErrors(prev => ({ ...prev, [tab.id]: '' }));

    // Show global loading overlay if using backend data
    if (!useMockData) {
      setIsBackendDataLoading(false);
    }

    try {
      // Collect all visualizations from main tab and sub-tabs
      const allVisualizations: any[] = [];

      // Add main tab visualizations (priority 1)
      if (tab.visualizations) {
        allVisualizations.push(...tab.visualizations);
      }

      // Add sub-tab visualizations with priority for the active sub-tab
      if (tab.subTabs && tab.subTabs.length > 0) {
        // activeSubTabIndex is the one the user is currently looking at
        const activeSubTab = tab.subTabs[activeSubTabIndex] || tab.subTabs[0];

        // Add active sub-tab visualizations (priority 2)
        if (activeSubTab.visualizations) {
          allVisualizations.push(...activeSubTab.visualizations);
        }
      }

      // 1. Mark all visualizations as loading for skeleton display
      setLoadingVisualizations(prev => {
        const next = { ...prev };
        const tabLoading: Record<string, boolean> = {};
        allVisualizations.forEach(viz => {
          tabLoading[viz.id] = true;
        });
        next[tab.id] = tabLoading;
        return next;
      });

      // UI Yield: Allow browser to paint the skeleton loaders before starting fetches
      await new Promise(resolve => setTimeout(resolve, 0));

      // 2. Start all fetches in parallel
      const fetchItems = allVisualizations
        .filter(viz => !!viz.dataQuery || !!viz.exampleResponse)
        .map(visualization => {
          const tabSpecificFilters = getTabSpecificFilters(tab.id);
          return {
            visualization,
            promise: smartDataManager.loadVisualizationData(
              visualization,
              tabSpecificFilters,
              false, // Don't force refresh for initial tab load
              getRequiredFields(visualization)
            )
          };
        });

      // 3. Process results one by one (incremental pop-in)
      for (const item of fetchItems) {
        if (!isMountedRef.current) break;
        const { visualization, promise } = item;

        try {
          const response = await promise;

          if (isMountedRef.current && response) {
            // Update tabData incrementally
            setTabData(prev => {
              const currentTabData = prev[tab.id] || {
                configId: config.id,
                tabId: tab.id,
                data: [],
                metadata: {
                  totalRecords: 0,
                  processingTime: 0,
                  cacheHit: false,
                  dataSource: config.dataSource,
                  lastRefreshed: new Date()
                },
                visualizations: {}
              };

              return {
                ...prev,
                [tab.id]: {
                  ...(currentTabData as any),
                  visualizations: {
                    ...((currentTabData as any).visualizations || {}),
                    [visualization.id]: response
                  },
                  metadata: {
                    ...currentTabData.metadata,
                    lastRefreshed: new Date()
                  }
                }
              };
            });

            // Mark this specific visualization as loaded
            setLoadingVisualizations(prev => ({
              ...prev,
              [tab.id]: {
                ...(prev[tab.id] || {}),
                [visualization.id]: false
              }
            }));

            // 3. UI Yielding
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        } catch (error) {
          console.error(`Failed to load data for visualization ${visualization.id}:`, error);
          setLoadingVisualizations(prev => ({
            ...prev,
            [tab.id]: {
              ...(prev[tab.id] || {}),
              [visualization.id]: false
            }
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load tab data:', error);

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setErrors(prev => ({
          ...prev,
          [tab.id]: error instanceof Error ? error.message : 'Failed to load data'
        }));
      }
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setLoading(prev => ({ ...prev, [tab.id]: false }));
        // Hide global loading overlay
        setIsBackendDataLoading(false);
      }
    }
  };

  // Load data when active tab changes - but only after filters are initialized
  useEffect(() => {
    // Load data for the active tab (this will include both main tab and sub-tab visualizations)
    if (activeTab && smartDataManager && filterValuesInitialized) {
      // 🔄 Update Data Set Framework with new active tab
      if (config.dataSets) {
        smartDataManager.setActiveTab(activeTab.id);
      }

      // Reset refresh state when switching tabs
      preventFilterLoopsRef.current = false;

      // Load data if we don't have it cached yet AND haven't already loaded it
      // Note: If loadedTabsRef was cleared (due to filter changes), this will reload the tab
      const tabId = activeTab.id;

      // 🔒 IN LIVE DATA MODE: The master pre-load effect handles ALL initial loading
      // This effect should ONLY run for:
      // 1. Mock data mode
      // 2. Subsequent tab switches AFTER initial load is complete
      if (!useMockData && dashboardKey) {
        // Skip if master load hasn't completed yet
        if (masterDataLoadingRef.current || !masterDataLoadComplete) {
          console.log('🔒 [Tab Load] Live mode: waiting for master load to complete');
          return;
        }

        // Skip if this is the initial tab that the master load already handled
        if (initialDataLoadStartedRef.current && loadedTabsRef.current.has(tabId)) {
          console.log('🔒 [Tab Load] Live mode: tab already loaded by master load');
          return;
        }
      }

      if (!tabData[tabId] || !loadedTabsRef.current.has(tabId)) {
        console.log(`📥 [Tab Load] Loading tab: ${tabId}`);
        loadTabData(activeTab).then(() => {
          loadedTabsRef.current.add(tabId);
        });
      }
    }
  }, [activeTab, smartDataManager, filterValuesInitialized, useMockData, dashboardKey]); // Wait for smartDataManager and filter initialization


  // Handle filter changes with refresh (with smart caching for live data)
  useEffect(() => {
    console.log(`🔄 [Filter Change Effect] TRIGGERED - checking conditions...`);
    console.log(`🔄 [Filter Change Effect] smartDataManager: ${!!smartDataManager}`);
    console.log(`🔄 [Filter Change Effect] filterValuesInitialized: ${filterValuesInitialized}`);
    console.log(`🔄 [Filter Change Effect] filterValues keys: ${Object.keys(filterValues).length}`);
    console.log(`🔄 [Filter Change Effect] masterDataLoadingRef: ${masterDataLoadingRef.current}`);
    console.log(`🔄 [Filter Change Effect] justUsedPreloadedDataRef: ${justUsedPreloadedDataRef.current}`);
    console.log(`🔄 [Filter Change Effect] preventFilterLoopsRef: ${preventFilterLoopsRef.current}`);
    console.log(`🔄 [Filter Change Effect] filterGenerationInProgressRef: ${filterGenerationInProgressRef.current}`);

    // Refresh visualizations when filters change, but use cached data in live mode
    if (smartDataManager && filterValuesInitialized && Object.keys(filterValues).length > 0) {
      // 🔒 Skip if master data loading is in progress - wait for atomic load to complete
      if (masterDataLoadingRef.current) {
        console.log('🔒 [Filter Change] Master loading in progress, skipping');
        return;
      }

      // Skip if we just used preloaded data (to prevent redundant backend calls)
      if (justUsedPreloadedDataRef.current) {
        console.log('🔒 [Filter Change] Just used preloaded data, skipping');
        return;
      }

      // Prevent loops during refresh operations
      if (preventFilterLoopsRef.current) {
        console.log('🔒 [Filter Change] preventFilterLoopsRef is true, skipping');
        return;
      }

      // Skip if filter generation is still in progress (filter values may be being initialized)
      if (filterGenerationInProgressRef.current) {
        console.log('🔒 [Filter Change] Filter generation in progress, skipping');
        return;
      }

      // CRITICAL: In live mode, detect and reset mock data filter values
      // This prevents filters from being reset to mock data after refresh
      if (!useMockData) {
        const mockLocationValues = ['Boston Downtown', 'Manhattan Central', 'Austin Downtown'];
        let hasMockDataValues = false;
        const correctedFilterValues: Record<string, any> = {};

        Object.entries(filterValues).forEach(([filterId, filterValue]) => {
          if (Array.isArray(filterValue) && filterValue.length > 0) {
            // Check if this filter contains mock location values
            const containsMockValues = filterValue.some((val: any) =>
              mockLocationValues.includes(val)
            );

            if (containsMockValues) {
              console.log(`⚠️ [Filter Change] Detected mock data values in ${filterId} in live mode - resetting to empty (select all)`);
              correctedFilterValues[filterId] = [];
              hasMockDataValues = true;
            }
          }
        });

        // If we found mock data values, reset them before refreshing
        if (hasMockDataValues) {
          console.log(`🔄 [Filter Change] Resetting mock data filter values:`, correctedFilterValues);
          preventFilterLoopsRef.current = true;
          setFilterValues(prev => ({
            ...prev,
            ...correctedFilterValues
          }));
          preventFilterLoopsRef.current = false;
          return; // Don't refresh yet - let the filter change effect run again with corrected values
        }
      }

      console.log(`✅ [Filter Change] All conditions passed - will refresh visualizations`);
      console.log(`✅ [Filter Change] Current filterValues:`, filterValues);

      // Invalidate all tab caches when filters change - force all tabs to reload
      loadedTabsRef.current.clear();
      setTabData({});
      setLoadingVisualizations({});

      // Add a small delay to prevent rapid-fire updates
      const timeoutId = setTimeout(() => {
        console.log(`🔄 [Filter Change] Timeout fired - starting refresh...`);
        if (!preventFilterLoopsRef.current) {
          preventFilterLoopsRef.current = true;

          // STRICT LAZY LOADING: Only refresh the active tab
          const refreshActiveTabOnly = async () => {
            const tabs = config.tabs || [];
            const activeTab = tabs[activeTabIndex];

            if (activeTab) {
              try {
                console.log(`🔄 [Filter Change] STRICT LAZY REFRESH - Starting active tab: ${activeTab.id}`);
                await refreshDataVisualizationsOnly(activeTab);
                // Mark ONLY active tab as loaded
                loadedTabsRef.current.add(activeTab.id);
                console.log(`✅ [Filter Change] Active tab ${activeTab.id} refreshed successfully`);
              } catch (error) {
                console.error(`❌ [Filter Change] Failed to refresh active tab ${activeTab.id}:`, error);
              }
            }

            console.log(`🔄 [Filter Change] Strict lazy refresh complete (background tabs invalidated)`);
            preventFilterLoopsRef.current = false;
          };

          refreshActiveTabOnly();
        } else {
          console.log(`🔒 [Filter Change] preventFilterLoopsRef became true during timeout, skipping`);
        }
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    } else {
      console.log(`🔒 [Filter Change] Conditions not met - no refresh`);
    }
  }, [filterValues, smartDataManager, useMockData, config.tabs]);

  // Handle data source changes (Mock vs Live)
  useEffect(() => {
    // Note: Data reload when switching between mock and live data is now handled by the
    // dynamic filters effect after it finishes generating options and validating filter values.
    // Calling loadTabData here causes a race condition where data loads before filters are validated.

    // Clear all cached data when switching data sources to ensure fresh load
    if (activeTab && filterValuesInitialized) {
      setTabData({});
    }
  }, [useMockData, filterValuesInitialized]);

  const handleSettingsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
  };

  const handleCopyValidationResults = () => {
    if (validationResult) {
      navigator.clipboard.writeText(formatResults());
    }
  };

  // Get available datasets for export
  const getAvailableDatasets = () => {
    const datasets: Array<{ id: string; name: string; endpoint: string }> = [];

    // Get actual registered datasets from DataSetManager
    if (smartDataManager) {
      const dataSetManager = (smartDataManager as any).dataSetManager;
      const registeredDatasets = dataSetManager.dataSets;

      debugLog(`🔧 [Export] Available registered datasets:`, Array.from(registeredDatasets.keys()));

      // Convert Map to array format
      registeredDatasets.forEach((dataset: any, id: string) => {
        if (dataset.mockDataConfig) { // Only show datasets with mock data config
          datasets.push({
            id: id, // Use actual internal ID
            name: dataset.name,
            endpoint: dataset.source?.endpoint || `/analytics/datasets/${id}`
          });
        }
      });
    }

    debugLog(`🔧 [Export] Formatted datasets for export:`, datasets);
    return datasets;
  };

  // Handle dataset export
  const handleDatasetExport = async (datasetId: string) => {
    if (!smartDataManager) return;

    setIsExporting(true);

    try {
      debugLog(`📊 [Export] Generating dataset export for: ${datasetId}`);

      // Generate comprehensive mock data for this specific dataset
      // This bypasses visualization filtering and gives raw dataset data
      const mockDataManager = (smartDataManager as any).dataSetManager;

      // Get the dataset configuration
      debugLog(`🔧 [Export] Looking for dataset: ${datasetId}`);
      debugLog(`🔧 [Export] Available datasets:`, Array.from(mockDataManager.dataSets.keys()));

      const dataset = mockDataManager.dataSets.get(datasetId);
      if (!dataset) {
        throw new Error(`Dataset ${datasetId} not found. Available datasets: ${Array.from(mockDataManager.dataSets.keys()).join(', ')}`);
      }

      if (!dataset.mockDataConfig) {
        throw new Error(`Dataset ${datasetId} has no mock data configuration`);
      }

      // Generate fresh mock data without filters for comprehensive export
      const exportData = await mockDataManager.mockDataGenerator.generateMockData(dataset, {});

      if (exportData.length === 0) {
        throw new Error('No mock data generated for dataset');
      }

      debugLog(`📊 [Export] Generated ${exportData.length} records for dataset: ${datasetId}`);

      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      const filename = `dataset-${datasetId}-${timestamp}.${exportFormat}`;

      // Export based on format
      if (exportFormat === 'csv') {
        exportToCSV(exportData, filename);
      } else if (exportFormat === 'json') {
        exportToDatasetJSON(exportData, datasetId, dataset, filename);
      } else if (exportFormat === 'xlsx') {
        exportToCSV(exportData, filename.replace('.xlsx', '.csv'));
      }

    } catch (error) {
      console.error('Dataset export failed:', error);
      alert(`Dataset export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle data-only export (raw data array)
  const handleDataOnlyExport = async (datasetId: string) => {
    if (!smartDataManager) return;

    setIsExporting(true);

    try {
      debugLog(`📊 [Export] Generating data-only export for: ${datasetId}`);

      const mockDataManager = (smartDataManager as any).dataSetManager;
      const dataset = mockDataManager.dataSets.get(datasetId);

      if (!dataset || !dataset.mockDataConfig) {
        throw new Error(`Dataset ${datasetId} not found or has no mock data configuration`);
      }

      // Generate fresh mock data without filters
      const exportData = await mockDataManager.mockDataGenerator.generateMockData(dataset, {});

      if (exportData.length === 0) {
        throw new Error('No mock data generated for dataset');
      }

      debugLog(`📊 [Export] Generated ${exportData.length} records for data-only export: ${datasetId}`);

      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      const filename = `data-${datasetId.replace(/[^a-zA-Z0-9-]/g, '-')}-${timestamp}.${exportFormat}`;

      // Export just the data array based on format
      if (exportFormat === 'csv') {
        exportToCSV(exportData, filename);
      } else if (exportFormat === 'json') {
        // For data-only JSON, export just the array
        const jsonContent = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      } else if (exportFormat === 'xlsx') {
        exportToCSV(exportData, filename.replace('.xlsx', '.csv'));
      }

    } catch (error) {
      console.error('Data-only export failed:', error);
      alert(`Data-only export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle data export (supports tab, dataset, and data-only modes)
  const handleDataExport = async () => {
    if (exportMode === 'dataset') {
      if (!selectedDataset) {
        alert('Please select a dataset to export');
        return;
      }
      return handleDatasetExport(selectedDataset);
    }

    if (exportMode === 'data-only') {
      if (!selectedDataset) {
        alert('Please select a dataset to export');
        return;
      }
      return handleDataOnlyExport(selectedDataset);
    }

    // Original tab export logic
    if (!activeTab || !smartDataManager) return;

    setIsExporting(true);

    try {
      // Get current dataset for the active tab
      const currentTabData = tabData[activeTab.id];
      if (!currentTabData) {
        throw new Error('No data available for export');
      }

      // Collect all data from visualizations
      const exportData: any[] = [];
      const visualizations = currentTabData.visualizations || {};

      // For each visualization, collect its data
      Object.entries(visualizations).forEach(([vizId, vizData]: [string, any]) => {
        if (vizData?.data && Array.isArray(vizData.data)) {
          // Add visualization context to each record
          vizData.data.forEach((record: any) => {
            exportData.push({
              ...record,
              _visualization_id: vizId,
              _tab_id: activeTab.id,
              _config_id: config.id,
              _exported_at: new Date().toISOString()
            });
          });
        }
      });

      if (exportData.length === 0) {
        throw new Error('No data available for export');
      }

      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      const filename = `${config.id}-${activeTab.id}-${timestamp}.${exportFormat}`;

      // Export based on format
      if (exportFormat === 'csv') {
        exportToCSV(exportData, filename);
      } else if (exportFormat === 'json') {
        exportToJSON(exportData, filename);
      } else if (exportFormat === 'xlsx') {
        // For now, fallback to CSV for XLSX until we add proper XLSX library
        exportToCSV(exportData, filename.replace('.xlsx', '.csv'));
      }

    } catch (error) {
      console.error('Export failed:', error);
      alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Export to CSV format
  const exportToCSV = (data: any[], filename: string) => {
    if (data.length === 0) return;

    // Get all unique keys from all records
    const allKeys = Array.from(new Set(data.flatMap(record => Object.keys(record))));

    // Create CSV header
    const csvHeader = allKeys.join(',');

    // Create CSV rows
    const csvRows = data.map(record =>
      allKeys.map(key => {
        const value = record[key];
        // Handle values that might contain commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    );

    // Combine header and rows
    const csvContent = [csvHeader, ...csvRows].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Export to JSON format
  const exportToJSON = (data: any[], filename: string) => {
    const jsonContent = JSON.stringify({
      exportInfo: {
        configId: config.id,
        tabId: activeTab?.id,
        exportedAt: new Date().toISOString(),
        recordCount: data.length,
        format: 'json',
        dataSource: useMockData ? 'mock' : 'live'
      },
      data: data
    }, null, 2);

    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Export dataset to JSON format with server integration metadata
  const exportToDatasetJSON = (data: any[], datasetId: string, dataset: any, filename: string) => {
    const jsonContent = JSON.stringify({
      exportInfo: {
        type: 'dataset',
        datasetId: datasetId,
        datasetName: dataset.name,
        configId: config.id,
        dashboardKey: dashboardKey,
        exportedAt: new Date().toISOString(),
        recordCount: data.length,
        format: 'json',
        dataSource: 'mock-template'
      },
      serverIntegration: {
        endpoint: dataset.source?.endpoint || `/analytics/datasets/${config.id}-dataset`,
        fullUrl: `${baseUrl}/${appKey}${dataset.source?.endpoint || `/analytics/datasets/${config.id}-dataset`}`,
        mockDataConfig: dataset.mockDataConfig,
        schema: dataset.mockDataConfig?.schema,
        instructions: [
          "1. Use this data as sample response for your server endpoint",
          "2. Replace mock data generation with your actual data logic",
          "3. Ensure your server response matches this structure",
          "4. Include 'success', 'data', and 'metadata' fields in response",
          "5. Test endpoint with: curl -H 'Authorization: Bearer {token}' " + `${baseUrl}/${appKey}${dataset.source?.endpoint || `/analytics/datasets/${config.id}-dataset`}`
        ]
      },
      sampleApiResponse: {
        success: true,
        data: data,
        metadata: {
          totalRecords: data.length,
          filteredRecords: data.length,
          serverFiltersApplied: [],
          cacheHit: false,
          processingTime: 120,
          dataVersion: "v1.0.0",
          dataSource: "server",
          lastRefreshed: new Date().toISOString()
        }
      }
    }, null, 2);

    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const settingsOpen = Boolean(settingsAnchor);

  // Handle chart-level filter changes (for charts like payment methods that have true filters)
  const handleChartFilterChange = (visualizationId: string, filterId: string, value: any) => {
    setChartFilterValues(prev => ({
      ...prev,
      [visualizationId]: {
        ...prev[visualizationId],
        [filterId]: value
      }
    }));
  };

  // Handle property editor save
  const handlePropertySave = (visualizationId: string, updates: Record<string, any>) => {
    if (!config.tabs || !onConfigUpdate) return;

    // Deep clone the config to avoid mutations
    const updatedConfig = JSON.parse(JSON.stringify(config));

    // Find and update the visualization in all tabs and sub-tabs
    updatedConfig.tabs.forEach((tab: any) => {
      // Update in main tab visualizations
      if (tab.visualizations) {
        tab.visualizations.forEach((viz: any) => {
          if (viz.id === visualizationId) {
            if (updates.config) {
              viz.config = { ...viz.config, ...updates.config };
            }
            if (updates.layout) {
              viz.layout = { ...viz.layout, ...updates.layout };
            }
          }
        });
      }

      // Update in sub-tab visualizations
      if (tab.subTabs) {
        tab.subTabs.forEach((subTab: any) => {
          if (subTab.visualizations) {
            subTab.visualizations.forEach((viz: any) => {
              if (viz.id === visualizationId) {
                if (updates.config) {
                  viz.config = { ...viz.config, ...updates.config };
                }
                if (updates.layout) {
                  viz.layout = { ...viz.layout, ...updates.layout };
                }
              }
            });
          }
        });
      }
    });

    // Notify parent component of the update
    onConfigUpdate(updatedConfig);
  };


  // Render visualization component
  // Reusable utility function to check if exactly N locations are selected
  // Handles hierarchical filters correctly by counting only leaf nodes (actual locations)
  const checkLocationCount = (locationFilterId: string, requiredCount: number): boolean => {
    if (!locationFilterId) return false;

    const locationFilterValue = filterValues[locationFilterId];

    if (!Array.isArray(locationFilterValue)) {
      return requiredCount === 0;
    }

    // Get the location filter config to check if it's hierarchical
    const locationFilter = config.filters?.find(f => f.filterId === locationFilterId);

    let selectedLocationCount = 0;

    if (locationFilter?.hierarchical && locationFilter?.hierarchyConfig) {
      // Count only values that don't have children (leaf nodes = actual locations)
      const allOptions = locationFilter.options || [];
      const parentValues = new Set(
        allOptions
          .filter((opt: any) => opt.children && opt.children.length > 0)
          .map(opt => opt.value)
      );
      selectedLocationCount = locationFilterValue.filter(val => !parentValues.has(val)).length;
    } else {
      // Non-hierarchical filter: simple count
      selectedLocationCount = locationFilterValue.length;
    }

    return selectedLocationCount === requiredCount;
  };

  /** Same gate as renderVisualization's requireSingleLocation early return — omit from layout so Grid cells are not reserved when the viz renders null. */
  const passesSingleLocationLayout = (visualization: any): boolean => {
    const vizConfig = visualization?.config;
    const requiresSingleLocation = vizConfig?.requireSingleLocation && vizConfig?.locationFilterId;
    if (!requiresSingleLocation) return true;
    if (visualization.type === 'mix-bar') return true;
    return checkLocationCount(vizConfig.locationFilterId, 1);
  };

  const renderVisualization = (visualization: any, data: any, tallHeight: boolean = false, cardCount: number = 3) => {
    const { type, config: vizConfig, title, description } = visualization;

    // Check if this visualization requires exactly 1 location to be selected
    // For mix-bar visualizations, we'll show a placeholder instead of hiding completely
    const requiresSingleLocation = vizConfig?.requireSingleLocation && vizConfig?.locationFilterId;
    const hasSingleLocation = requiresSingleLocation ? checkLocationCount(vizConfig.locationFilterId, 1) : true;

    if (requiresSingleLocation && !hasSingleLocation && type !== 'mix-bar') {
      return null; // Hide non-mix-bar visualizations that require single location
    }

    // Only warn about missing data for visualizations that need it
    if (!data && visualization.dataQuery) {
      //  console.warn('No data provided for visualization:', visualization.id);
    }

    const tabId = currentTab?.id || (data?.tabId) || '';
    // FIXED: Only use individual card loading state to allow sequential pop-in
    // loading[tabId] was acting as a master block that forced everything to resolve at once
    const serverData = data?.visualizations?.[visualization.id]?.data;
    const hasServerData = Array.isArray(serverData) && serverData.length > 0;
    const perVizLoadingFlag = loadingVisualizations[tabId]?.[visualization.id];
    // Same gate as loadTabData: only these viz participate in fetch. Before the first effect run,
    // perVizLoadingFlag is undefined — treat as loading so we never flash exampleResponse/zeros.
    const participatesInDataFetch = !!(visualization.dataQuery || visualization.exampleResponse);
    const isVizLoading = participatesInDataFetch
      ? perVizLoadingFlag !== false && !hasServerData
      : !!perVizLoadingFlag;

    // CRITICAL: If we are actively loading, DON'T fall back to example response, show nothing so skeleton triggers
    const visualizationData = (serverData && serverData.length > 0)
      ? serverData
      : (isVizLoading ? [] : (visualization.exampleResponse || []));

    const commonProps = {
      loading: isVizLoading,
      error: errors[tabId]
    };

    const isFetchCompleteEmpty =
      participatesInDataFetch &&
      perVizLoadingFlag === false &&
      !hasServerData &&
      !errors[tabId];

    if (isFetchCompleteEmpty) {
      return (
        <VisualizationEmptyState
          title={title}
          description={description}
          vizType={type}
          tallHeight={tallHeight}
        />
      );
    }

    // Add safety check specifically for table type
    if (type === 'table') {

      if (!vizConfig) {
        return (
          <Paper sx={{ p: 2, height: '100%' }}>
            <Alert severity="error">
              Configuration missing for table: {title}
            </Alert>
          </Paper>
        );
      }

      // Use smart data manager for intelligent column filtering
      let modifiedConfig = vizConfig;

      // Apply column filtering for tables with conditional column visibility
      // This is generic - works for any table with conditional columns, not domain-specific
      const hasConditionalColumns = vizConfig.columns?.some((col: any) => col.conditional?.showWhen);
      if (smartDataManager && hasConditionalColumns) {
        try {
          // Implement intelligent column filtering based on conditional visibility
          const filteredColumns = vizConfig.columns.filter((column: any) => {
            // If column has no conditional visibility, always show it
            if (!column.conditional?.showWhen) {
              return true;
            }

            const { filterId, includes } = column.conditional.showWhen;
            const filterValue = filterValues[filterId];

            // Hide if filter not set
            if (filterValue === null || filterValue === undefined) {
              return false;
            }

            // Hide if filter is not an array
            if (!Array.isArray(filterValue)) {
              return false;
            }

            // Hide if filter is an empty array (nothing selected)
            if (filterValue.length === 0) {
              return false;
            }

            // Show if any of the required values are included in the filter
            return includes.some((requiredValue: string) => filterValue.includes(requiredValue));
          });

          modifiedConfig = {
            ...vizConfig,
            columns: filteredColumns
          };
        } catch (error) {
          console.warn('Failed to apply column filtering:', error);
          // Fallback to original config if filtering fails
          modifiedConfig = vizConfig;
        }
      }

      // Render table with extra error boundary
      try {
        // PERFORMANCE: Defer rendering for heavy hierarchical tables (>100 rows)
        const isHeavyTable = vizConfig?.hierarchical &&
          Array.isArray(visualizationData) &&
          visualizationData.length > 100;

        // Pass the full server data for variance calculations (includes historical comparison data)
        const fullDataset = serverData || visualizationData;

        return (
          <DeferredTableRenderer
            visualization={visualization}
            visualizationData={visualizationData}
            modifiedConfig={modifiedConfig}
            commonProps={commonProps}
            isHeavy={isHeavyTable}
            filterValues={filterValues}
            dateFilterId={vizConfig?.dateFilterId || 'global-date-range'}
            allVisualizationData={fullDataset}
            highlightDeviations={highlightDeviations}
            setHighlightDeviations={setHighlightDeviations}
            varianceType={varianceType}
            setVarianceType={setVarianceType}
            varianceDisplayMode={varianceDisplayMode}
            setVarianceDisplayMode={setVarianceDisplayMode}
            isVarianceRecalculating={isVarianceRecalculating}
            showCreditCardDetail={showCreditCardDetail}
            setShowCreditCardDetail={handleCreditCardDetailChange}
            showThirdPartyDetail={showThirdPartyDetail}
            setShowThirdPartyDetail={handleThirdPartyDetailChange}
            showAllPaymentDetails={showAllPaymentDetails}
            setShowAllPaymentDetails={setShowAllPaymentDetails}
            onVarianceTypeChange={handleVarianceTypeChange}
            onVarianceDisplayModeChange={handleVarianceDisplayModeChange}
          />
        );
      } catch (error) {
        console.error('Error rendering table:', error);
        return (
          <Paper sx={{ p: 2, height: '100%' }}>
            <Alert severity="error">
              Error rendering table: {title}
              <br />
              {error instanceof Error ? error.message : 'Unknown error'}
            </Alert>
          </Paper>
        );
      }
    }

    switch (type) {
      case 'kpi': {
        return (
          <KpiVisualizationRenderer
            visualization={visualization}
            data={data}
            filterValues={filterValues}
            commonProps={commonProps}
            isVizLoading={isVizLoading}
            tabId={tabId}
            errors={errors}
            tallHeight={tallHeight}
            cardCount={cardCount}
            getNetSalesForOrderType={getNetSalesForOrderType}
          />
        );
      }

      case 'chart':
        // Only pass toggle props to payment channel chart
        const isPaymentChannelChart = visualization.id === 'payment-type-mix-chart' ||
          (vizConfig?.chartType === 'ranked-band-line' && vizConfig?.paymentChannelGrouping?.enabled);

        return (
          <Box sx={{
            height: '100%',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}>
            <AnalyticsChart
              data={visualizationData}
              config={vizConfig || {}}
              visualization={visualization}
              filterValues={chartFilterValues[visualization.id] || {}}
              onFilterChange={(filterId: string, value: any) =>
                handleChartFilterChange(visualization.id, filterId, value)
              }
              {...(isPaymentChannelChart ? {
                showCreditCardDetail,
                showThirdPartyDetail,
                onCreditCardDetailChange: handleCreditCardDetailChange,
                onThirdPartyDetailChange: handleThirdPartyDetailChange
              } : {})}
              {...commonProps}
            />
          </Box>
        );

      case 'mix-bar': {
        const mixConfig = vizConfig as any;

        // Check if single location is required and not met
        const requiresSingleLocation = mixConfig?.requireSingleLocation && mixConfig?.locationFilterId;
        const hasSingleLocation = requiresSingleLocation ? checkLocationCount(mixConfig.locationFilterId, 1) : true;

        if (requiresSingleLocation && !hasSingleLocation) {
          // Show placeholder card instead of hiding completely
          // Match MixBarCard styling: same height, border, padding, and background
          const cardHeight = tallHeight ? '12rem' : '10.5rem';

          return (
            <Box
              sx={{
                height: cardHeight,
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: '0.5rem',
                padding: '1rem',
                backgroundColor: theme.palette.background.paper,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s ease',
              }}
            >
              <InfoIcon sx={{ fontSize: 32, color: theme.palette.text.secondary }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: tallHeight ? '0.875rem' : '0.75rem',
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: tallHeight ? '0.8rem' : '0.7rem',
                  textAlign: 'center',
                  maxWidth: '90%',
                }}
              >
                Please select exactly one location to view the mix breakdown.
              </Typography>
            </Box>
          );
        }

        // visualizationData contains segments from backend processing
        const segments = visualizationData || [];

        console.log(`[Mix Bar] ${visualization.id}: Rendering with data`, {
          segmentCount: segments.length,
          segments,
          visualizationData,
          loading: isVizLoading
        });

        // Calculate variance if we have historical data
        let variance = undefined;
        let yoy = undefined;

        // For now, we'll implement variance/yoy later if historical data is available
        // This would require comparing current vs previous period segment totals

        return (
          <MixBarCard
            title={title}
            subtitle={description}
            segments={segments}
            variance={variance}
            yoy={yoy}
            loading={isVizLoading}
            format={mixConfig?.format || 'percentage'}
            showLabels={mixConfig?.showLabels !== false}
            segmentMinWidth={mixConfig?.segmentMinWidth || 10}
            tallHeight={tallHeight}
            cardCount={cardCount}
          />
        );
      }

      case 'performance-card':
        // Check if this is a register performance card and hide if less than 2 registers
        const isRegisterPerformanceCard = vizConfig?.displayField === 'register' ||
          vizConfig?.sourceVisualizationId === 'register-performance-table' ||
          (vizConfig?.sourceVisualizationId && visualization.id?.includes('register'));

        if (isRegisterPerformanceCard) {
          // Don't hide while loading - wait for data to determine register count
          const isLoading = loading[currentTab?.id || ''];

          if (!isLoading) {
            let registerCount = 0;

            // Try to get register count from source visualization data first
            if (vizConfig?.sourceVisualizationId) {
              const sourceVizId = vizConfig.sourceVisualizationId;
              const sourceData = data?.visualizations?.[sourceVizId]?.data || [];

              // Count unique registers in the source data
              // For hierarchical tables, we need to filter out parent rows (those with hasChildren: true)
              const registerRows = sourceData.filter((row: any) => {
                // Include rows that have a register field and are not parent rows
                return row.register && !row.hasChildren;
              });

              // Get unique register values
              const uniqueRegisters = new Set(
                registerRows.map((row: any) => row.register).filter(Boolean)
              );

              registerCount = uniqueRegisters.size;
            }

            // Fallback: check visualizationData if source data not available
            if (registerCount === 0 && Array.isArray(visualizationData) && visualizationData.length > 0) {
              // Check if visualizationData contains register information
              const registersInData = new Set(
                visualizationData
                  .map((row: any) => row.register)
                  .filter(Boolean)
              );
              registerCount = registersInData.size;
            }

            // Hide card if less than 2 registers (only when not loading and we have data)
            if (registerCount > 0 && registerCount < 2) {
              return null;
            }
          }
        }

        return (
          <PerformanceCard
            data={visualizationData}
            config={vizConfig || {}}
            type={vizConfig?.rankingType === 'highest' ? 'top' : 'bottom'}
            title={title}
            tallHeight={tallHeight}
            cardCount={cardCount}
            {...commonProps}
          />
        );

      case 'placeholder':
        return (
          <Paper sx={{ p: 4, height: '100%', textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            {description && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {description}
              </Typography>
            )}
            <Typography variant="body1" sx={{ mt: 2 }}>
              {vizConfig?.message || 'Content coming soon...'}
            </Typography>
          </Paper>
        );

      case 'trend-card':
        const primaryValue = visualizationData[0]?.[vizConfig?.primaryMetric] || 0;
        const secondaryValue = visualizationData[0]?.[vizConfig?.secondaryMetric] || 0;

        return (
          <Paper sx={{ p: 2, height: '100%', bgcolor: 'primary.main', color: 'white' }}>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {typeof primaryValue === 'number' ? primaryValue.toLocaleString() : primaryValue}
            </Typography>
            {vizConfig?.secondaryMetric && (
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {vizConfig?.trendPeriod}: {typeof secondaryValue === 'number' ? secondaryValue.toLocaleString() : secondaryValue}
              </Typography>
            )}
          </Paper>
        );

      case 'filter':
        // Handle different filter types based on configuration
        if (vizConfig?.type === 'number-range') {
          const filterId = vizConfig?.filterId || 'number-range';
          const currentRangeValue = filterValues[filterId] || vizConfig?.defaultValue || { min: null, max: null };

          // Get source data for range calculation
          const sourceVizId = vizConfig?.sourceVisualizationId;
          let sourceData: any[] = [];
          if (sourceVizId && data?.visualizations?.[sourceVizId]) {
            sourceData = data.visualizations[sourceVizId].data || [];
          }

          return (
            <NumberRangeFilter
              min={currentRangeValue.min}
              max={currentRangeValue.max}
              onChange={(min, max) => {
                handleFilterChange({ [filterId]: { min, max } });
              }}
              disabled={false}
              label={title}
              placeholder={vizConfig?.placeholder}
              clearable={vizConfig?.clearable !== false}
              targetField={vizConfig?.targetField}
              sourceData={sourceData}
            />
          );
        }

        // Handle search filters
        if (vizConfig?.type === 'search') {
          const filterId = vizConfig?.filterId || 'search';
          const currentSearchValue = filterValues[filterId] || vizConfig?.defaultValue || '';

          return (
            <Paper sx={{ p: 2, bgcolor: 'grey.900', borderRadius: 1, color: 'white', height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 2 }}>
                {title}
              </Typography>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label={vizConfig?.label || title}
                placeholder={vizConfig?.placeholder}
                value={currentSearchValue}
                onChange={(e) => {
                  handleFilterChange({ [filterId]: e.target.value });
                }}
                disabled={false}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey.600',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey.400',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'grey.400',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'grey.400',
                    opacity: 1,
                  },
                }}
              />
            </Paper>
          );
        }

        // Handle checkbox filters
        if (vizConfig?.type === 'checkbox') {
          const filterId = vizConfig?.filterId || 'checkbox';
          const currentCheckboxValue = filterValues[filterId] || vizConfig?.defaultValue || false;

          return (
            <Paper sx={{ p: 2, bgcolor: 'grey.900', borderRadius: 1, color: 'white', height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 2 }}>
                {title}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(currentCheckboxValue)}
                    onChange={(e) => {
                      handleFilterChange({ [filterId]: e.target.checked });
                    }}
                    disabled={false}
                    sx={{
                      color: 'grey.400',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      },
                    }}
                  />
                }
                label={vizConfig?.label || title}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    color: 'white',
                    fontSize: '0.875rem',
                  },
                }}
              />
            </Paper>
          );
        }

        // Default filter (multi-select)
        // CRITICAL: Read filter options from dynamicFilters state, not from vizConfig
        // This ensures we use the current filter options (live data) instead of stale mock data options
        const filterId = (type === 'filter') ? (vizConfig as FilterConfig)?.filterId || 'filter' : 'filter';
        const currentFilterConfig = dynamicFilters.find((f: any) => f.filterId === filterId);
        let filterOptions = currentFilterConfig?.options || vizConfig?.options || [];

        // For hierarchical filters with dynamic options, don't initialize silently 
        // as they need to build options from data first
        const isHierarchicalDynamic = (type === 'filter') &&
          (vizConfig as FilterConfig)?.hierarchical &&
          (vizConfig as FilterConfig)?.optionsSource === 'dynamic';

        // Initialize filter only for static filters to avoid triggering reloads during render
        // CRITICAL: Skip initialization if filter generation is in progress (prevents using stale mock data options)
        if (filterValues[filterId] === undefined && !isHierarchicalDynamic && !preventFilterLoopsRef.current && !filterGenerationInProgressRef.current) {
          let defaultValue = vizConfig?.defaultValue || [];

          // In mock mode, auto-select all options if no default value is specified
          // This ensures mock data displays by default (small dataset, no performance concern)
          if (useMockData && Array.isArray(filterOptions) && filterOptions.length > 0 && (!defaultValue || defaultValue.length === 0)) {
            // Extract all option values to auto-select
            defaultValue = filterOptions.map((opt: any) => typeof opt === 'string' ? opt : opt.value);
          }

          setTimeout(() => initializeFilterSilently(filterId, defaultValue), 0);
        }

        // In mock mode, auto-select all options if filter value is empty or undefined
        // This ensures mock data displays by default when switching to mock mode or on fresh load
        // Also handles cases where empty arrays were loaded from localStorage (now treated as "no selection")
        // CRITICAL: Only run this in mock mode AND when filter generation is NOT in progress
        // CRITICAL: NEVER run this in live mode - empty arrays mean "select all" in live mode
        // This prevents resetting filters to mock data values when switching to live mode or after refresh
        const existingValue = filterValues[filterId];
        const isEmptyOrUndefined = existingValue === undefined ||
          (Array.isArray(existingValue) && existingValue.length === 0);
        // Only auto-select in mock mode - in live mode, empty arrays are correct (select all)
        if (useMockData && !isHierarchicalDynamic && !preventFilterLoopsRef.current && !filterGenerationInProgressRef.current &&
          Array.isArray(filterOptions) && filterOptions.length > 0 &&
          isEmptyOrUndefined) {
          const allValues = filterOptions.map((opt: any) => typeof opt === 'string' ? opt : opt.value);
          setTimeout(() => handleFilterChange({ [filterId]: allValues }), 0);
        }
        // In live mode, if filter is empty, that's correct - don't change it
        // This prevents the render logic from resetting filters to mock data after refresh

        const currentFilterValue = filterValues[filterId] || vizConfig?.defaultValue || [];

        // Handle dynamic hierarchical filters
        if (type === 'filter' && visualization.dataSet) {
          const filterConfig = vizConfig as FilterConfig;
          if (filterConfig?.optionsSource === 'dynamic' && filterConfig?.hierarchical && filterConfig?.hierarchyConfig) {


            // Extract the actual data array from the filter data structure
            let actualData = data;


            if (data && !Array.isArray(data)) {
              // For filters, data might be wrapped in visualizations structure
              if (data.visualizations && data.visualizations[visualization.id]) {
                actualData = data.visualizations[visualization.id].data;

              } else if (data.data) {
                actualData = data.data;

              }
            }



            // CRITICAL FIX: Always rebuild for live data, use cache only for mock data stability
            const shouldUseCache = useMockData && hierarchicalFilterOptionsCache[filterId];

            if (shouldUseCache) {
              // Use cached options (mock data only - prevents disappearing when filters change)
              filterOptions = hierarchicalFilterOptionsCache[filterId];
            } else if (actualData && Array.isArray(actualData) && actualData.length > 0) {
              // Build fresh options from current data
              try {
                filterOptions = convertHierarchyConfigToFilterOptions(filterConfig.hierarchyConfig, actualData);


                // Cache only for mock data (live data should always rebuild fresh)
                if (useMockData) {
                  setHierarchicalFilterOptionsCache(prev => ({
                    ...prev,
                    [filterId]: filterOptions
                  }));
                }

                // Auto-select all locations only for mock data (small dataset)
                // For live data, leave filter empty to allow user selection
                if (useMockData && (!currentFilterValue || currentFilterValue.length === 0) && !preventFilterLoopsRef.current) {
                  // Mock data only: Auto-select all leaf nodes for convenience (small dataset)
                  const allLeafValues: string[] = [];
                  const extractLeafValues = (options: any[]) => {
                    options.forEach(option => {
                      if (option.children && option.children.length > 0) {
                        extractLeafValues(option.children);
                      } else {
                        allLeafValues.push(option.value);
                      }
                    });
                  };
                  extractLeafValues(filterOptions);

                  // Auto-select in mock data, but prevent during refresh cycles
                  setTimeout(() => {
                    if (!preventFilterLoopsRef.current) {
                      handleFilterChange({ [filterId]: allLeafValues });
                    }
                  }, 0);
                }
                // Live data: Don't auto-select anything (filter stays empty)
              } catch (error) {
                console.warn(`[TabbedAnalyticsDashboard] Failed to build hierarchical options for ${filterId}:`, error);
                filterOptions = [];
              }
            } else {
              debugLog(`[TabbedAnalyticsDashboard] No valid array data available for hierarchical filter ${filterId}`);
            }
          }
        }

        return (
          <Paper sx={{ p: 0, height: '100%' }}>
            <UnifiedFilterComponent
              key={`${filterId}-${filterOptions.length}-${useMockData}`} // Force remount when options or data source changes
              title={title}
              options={filterOptions}
              value={currentFilterValue}
              placeholder={vizConfig?.placeholder}
              onChange={(newValues) => {
                handleFilterChange({ [filterId]: newValues });
              }}
              disabled={false}  // Keep filters always interactive
              allowSelectAll={vizConfig?.allowSelectAll !== false}
              hierarchical={(type === 'filter') ? (vizConfig as FilterConfig)?.hierarchical || false : false}
              maxSelection={(type === 'filter' && (vizConfig as FilterConfig)?.hierarchical) ? 6 : undefined} // Limit hierarchical location filters to 6 selections
              defaultExpanded={(type === 'filter') ? (vizConfig as FilterConfig)?.defaultExpanded || false : false}
            />
          </Paper>
        );

      case 'date-range-filter':
        const dateFilterId = vizConfig?.filterId || 'date-range';

        const currentDateFilter = filterValues[dateFilterId] || vizConfig?.defaultValue || { start: null, end: null };

        // Convert string dates to Date objects if needed
        // Handle date constants (like "LAST_QUARTER", "TODAY") by resolving them first
        const resolveDate = (dateValue: any): Date | null => {
          if (!dateValue) return null;
          if (dateValue instanceof Date) return dateValue;
          if (typeof dateValue === 'string') {
            // Check if it's a date constant
            if (DateConstantsService.isDateConstant(dateValue)) {
              const resolvedDateString = DateConstantsService.resolveDateConstant(dateValue);
              return new Date(resolvedDateString);
            }
            // Otherwise treat as regular date string
            return new Date(dateValue);
          }
          return null;
        };

        const startDate = resolveDate(currentDateFilter.start);
        const endDate = resolveDate(currentDateFilter.end);
        const startTime = currentDateFilter.startTime;
        const endTime = currentDateFilter.endTime;

        return (
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            startTime={startTime}
            endTime={endTime}
            onChange={(startDate, endDate, startTime, endTime) => {
              const value: any = { start: startDate, end: endDate };
              if (startTime !== undefined) value.startTime = startTime;
              if (endTime !== undefined) value.endTime = endTime;
              handleFilterChange({ [dateFilterId]: value });
            }}
            disabled={false}  // Keep filters always interactive
            label={title}
            showQuickButtons={vizConfig?.showQuickButtons !== false}
          />
        );

      case 'map':
        return (
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            {description && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {description}
              </Typography>
            )}
            <GeographicMap
              data={visualizationData}
              config={vizConfig || {}}
              {...commonProps}
            />
          </Paper>
        );

      case 'ai-insight':
        // Debug: Log config to help diagnose backgroundColor loading issues
        if (process.env.NODE_ENV === 'development' && vizConfig?.backgroundColor) {
          console.log(`[TabbedAnalyticsDashboard] AI Insight config backgroundColor:`, vizConfig.backgroundColor, 'for visualization:', visualization.id);
        }
        const aiInsightCard = (
          <AiInsightCard
            config={vizConfig || {}}
            title={title}
            dashboardFilters={filterValues}
            dashboardMetadata={{
              name: config.name,
              description: config.description,
              dateRange: filterValues['date-range'] || null,
            }}
            isLiveMode={!useMockData}
            dashboardKey={dashboardKey}
            dashboardId={config.id}
            insightCardId={visualization.id}
          />
        );

        // Note: Visualization decorator wrapping disabled (wrapWithDecorator undefined)
        // This would wrap with decorator in preview mode AND mock data mode
        return aiInsightCard;

      default:
        return (
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography>Unsupported visualization type: {type}</Typography>
          </Paper>
        );
    }
  };

  // Check if a visualization should be visible based on conditional configuration
  // Helper function to check if a register performance card should be shown
  // Returns false if it's a register performance card with less than 2 registers
  const shouldShowRegisterPerformanceCard = (visualization: any, tabData: any): boolean => {
    const vizConfig = visualization.config;

    // Check if this is a register performance card
    const isRegisterPerformanceCard = vizConfig?.displayField === 'register' ||
      vizConfig?.sourceVisualizationId === 'register-performance-table' ||
      (vizConfig?.sourceVisualizationId && visualization.id?.includes('register'));

    if (!isRegisterPerformanceCard) {
      return true; // Not a register performance card, show it
    }

    // Check if we're currently loading - if so, show the card (will show loading state)
    const isLoading = loading[currentTab?.id || ''];
    if (isLoading) {
      return true;
    }

    let registerCount = 0;

    // Try to get register count from source visualization data first
    if (vizConfig?.sourceVisualizationId) {
      const sourceVizId = vizConfig.sourceVisualizationId;
      const sourceData = tabData?.visualizations?.[sourceVizId]?.data || [];

      // Count unique registers in the source data
      // For hierarchical tables, we need to filter out parent rows (those with hasChildren: true)
      const registerRows = sourceData.filter((row: any) => {
        // Include rows that have a register field and are not parent rows
        return row.register && !row.hasChildren;
      });

      // Get unique register values
      const uniqueRegisters = new Set(
        registerRows.map((row: any) => row.register).filter(Boolean)
      );

      registerCount = uniqueRegisters.size;
    }

    // Fallback: check visualizationData if source data not available
    if (registerCount === 0) {
      const serverData = tabData?.visualizations?.[visualization.id]?.data;
      const visualizationData = (serverData && serverData.length > 0) ? serverData : (visualization.exampleResponse || []);

      if (Array.isArray(visualizationData) && visualizationData.length > 0) {
        // Check if visualizationData contains register information
        const registersInData = new Set(
          visualizationData
            .map((row: any) => row.register)
            .filter(Boolean)
        );
        registerCount = registersInData.size;
      }
    }

    // Hide card if less than 2 registers (only when we have data and know the count)
    return registerCount === 0 || registerCount >= 2;
  };

  const shouldShowVisualization = (visualization: any): boolean => {
    const config = visualization.config;
    if (!config?.conditional?.showWhen) {
      return true; // Show by default if no conditional logic
    }

    const { filterId, includes } = config.conditional.showWhen;
    const filterValue = filterValues[filterId];

    // Hide if filter not set
    if (filterValue === null || filterValue === undefined) {
      return false;
    }

    // Hide if filter is not an array
    if (!Array.isArray(filterValue)) {
      return false;
    }

    // Hide if filter is an empty array (nothing selected)
    if (filterValue.length === 0) {
      return false;
    }

    // Check if any of the required values are included in the filter
    return includes.some((requiredValue: string) => filterValue.includes(requiredValue));
  };

  /**
   * Render visualizations grid
   * 
   * Layout Strategy:
   * - If visualizations have explicit `layout.order` configured: Uses configuration-driven Grid layout
   *   sorted by order, respecting `layout.span` for column widths. This is the preferred approach
   *   for new dashboards as it's flexible and not tied to specific visualization types.
   * 
   * - If no explicit ordering: Falls back to legacy hardcoded layout that groups by type
   *   (KPIs/Performance cards first row, charts full width, etc.). This maintains backward
   *   compatibility with existing dashboards but is less flexible.
   * 
   * TODO: Consider migrating all dashboards to use explicit layout.order/span and removing
   *       the legacy hardcoded layout logic.
   */
  const renderVisualizationsGrid = (visualizations: any[], tabData: any) => {
    if (!visualizations || visualizations.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            No visualizations configured
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please configure visualizations for this tab
          </Typography>
        </Paper>
      );
    }

    // Filter visualizations based on conditional logic
    // Note: Domain-specific filtering (e.g., hiding specific titles) should be done via
    // conditional visibility in the visualization config, not hardcoded here
    const visibleVisualizations = visualizations
      .filter(shouldShowVisualization)
      .filter(viz => {
        // Also filter out register performance cards with less than 2 registers
        if (viz.type === 'performance-card') {
          return shouldShowRegisterPerformanceCard(viz, tabData);
        }
        return true;
      })
      .filter(passesSingleLocationLayout);

    // Auto-inject layout.order and layout.span for visualizations without explicit layout
    // This maintains backward compatibility while enabling configuration-driven layout
    // Groups by type: cards (kpi/performance-card/ai-insight) → charts → tables → others
    const cardTypes = ['kpi', 'performance-card', 'ai-insight'];

    // Separate visualizations into groups by type (only those without explicit layout)
    const cardsWithoutLayout = visibleVisualizations.filter(v =>
      cardTypes.includes(v.type) && (v.layout?.order === undefined || v.layout?.order === null)
    );
    const chartsWithoutLayout = visibleVisualizations.filter(v =>
      v.type === 'chart' && (v.layout?.order === undefined || v.layout?.order === null)
    );
    const tablesWithoutLayout = visibleVisualizations.filter(v =>
      v.type === 'table' && (v.layout?.order === undefined || v.layout?.order === null)
    );
    const othersWithoutLayout = visibleVisualizations.filter(v =>
      !cardTypes.includes(v.type) && v.type !== 'chart' && v.type !== 'table' &&
      (v.layout?.order === undefined || v.layout?.order === null)
    );

    // Separate visualizations by type for custom ordering
    const totalSalesCard = visibleVisualizations.find(viz =>
      viz.type === 'kpi' && (viz.title?.toLowerCase().includes('total sales') || viz.title?.toLowerCase().includes('net sales'))
    );
    const performerCards = visibleVisualizations.filter(viz =>
      viz.type === 'performance-card'
    );
    const aiInsightCards = visibleVisualizations.filter(viz => viz.type === 'ai-insight');
    // Debug: Log AI insight cards detection
    if (aiInsightCards.length > 0) {
      console.log('[TabbedAnalyticsDashboard] AI Insight Cards detected:', aiInsightCards.length, aiInsightCards.map(v => ({ id: v.id, title: v.title })));
    }
    const chartVisualizations = visibleVisualizations.filter(viz =>
      viz.type === 'chart'
    );

    // Collect all KPI cards first (so we can exclude them from otherVisualizations)
    const allKpiCards = visibleVisualizations.filter(viz => viz.type === 'kpi' || viz.type === 'mix-bar');
    const otherKpiCards = allKpiCards.filter(viz => viz !== totalSalesCard);

    const otherVisualizations = visibleVisualizations.filter(viz =>
      viz !== totalSalesCard &&
      !performerCards.includes(viz) &&
      !chartVisualizations.includes(viz) &&
      !allKpiCards.includes(viz) && // Exclude all KPI cards to prevent duplication
      !aiInsightCards.includes(viz) // Exclude AI insight cards to prevent duplication
    );

    // Build ordered list: Total Sales, Top Performer, Low Performer, then charts (full width), then others
    const orderedVisualizations = [];
    if (totalSalesCard) orderedVisualizations.push(totalSalesCard);
    orderedVisualizations.push(...performerCards);
    orderedVisualizations.push(...chartVisualizations);
    orderedVisualizations.push(...otherVisualizations);

    // Calculate card counts separately for KPI and performance rows
    const kpiCardCount = allKpiCards.length + (renderAdditionalCard ? 1 : 0);
    const performerCardCount = performerCards.length;
    const kpiHasTallHeight = kpiCardCount >= 5;
    const performerHasTallHeight = performerCardCount >= 5;

    return (
      <>
        {/* First row: All KPI Cards (max 4 per row, wraps to next row) */}
        {allKpiCards.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              flexWrap: { xs: 'nowrap', md: 'wrap' },
              gap: { xs: 2, md: '0.5rem' },
              mt: 1,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              overflow: 'visible',
              boxSizing: 'border-box',
              paddingLeft: '8px',
              paddingRight: '8px',
            }}
          >
            {/* Total Sales Card */}
            {totalSalesCard && (
              <Box
                key={totalSalesCard.id}
                sx={{
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: { xs: '100%', md: 'calc(25% - 0.375rem)' },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  minHeight: kpiHasTallHeight ? '9.5rem' : '7.625rem',
                  boxSizing: 'border-box',
                }}
              >
                {renderVisualization(totalSalesCard, tabData, kpiHasTallHeight, kpiCardCount)}
              </Box>
            )}
            {otherKpiCards.map((visualization, index) => (
              <Box
                key={visualization.id}
                sx={{
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: { xs: '100%', md: 'calc(25% - 0.375rem)' },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  minHeight: kpiHasTallHeight ? '9.5rem' : '7.625rem',
                  boxSizing: 'border-box',
                }}
              >
                {renderVisualization(visualization, tabData, kpiHasTallHeight, kpiCardCount)}
              </Box>
            ))}
            {/* Additional card (e.g., Insights Card) */}
            {renderAdditionalCard && (
              <Box
                sx={{
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: { xs: '100%', md: 'calc(25% - 0.375rem)' },
                }}
              >
                {renderAdditionalCard()}
              </Box>
            )}
          </Box>
        )}

        {/* Explanatory text for zone and revenue-center tabs */}
        {allKpiCards.length > 0 && performerCards.length > 0 && (tabData?.tabId === 'zone' || tabData?.tabId === 'revenue-center') && (
          <Box
            sx={{
              mt: 1.5,
              mb: 0.5,
              px: '8px',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#71717a',
                fontSize: '0.875rem',
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Note: {tabData?.tabId === 'zone' ? 'Zones' : 'Revenue centers'} that are not registered in the system will be grouped and displayed under the "default" category.
            </Typography>
          </Box>
        )}

        {/* Second row: Performance Cards (max 4 per row, wraps to next row) */}
        {performerCards.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              flexWrap: { xs: 'nowrap', md: 'wrap' },
              gap: { xs: 2, md: '0.5rem' },
              mt: allKpiCards.length > 0 ? 1 : 1,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              overflow: 'visible',
              boxSizing: 'border-box',
              paddingLeft: '8px',
              paddingRight: '8px',
            }}
          >
            {performerCards.map((visualization, index) => (
              <Box
                key={visualization.id}
                sx={{
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: { xs: '100%', md: 'calc(25% - 0.375rem)' },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  minHeight: performerHasTallHeight ? '9.5rem' : '7.625rem',
                  boxSizing: 'border-box',
                }}
              >
                {renderVisualization(visualization, tabData, performerHasTallHeight, performerCardCount)}
              </Box>
            ))}
          </Box>
        )}

        {/* AI Insight Cards Row - Separate row for AI insight cards */}
        {aiInsightCards.length > 0 && (() => {
          console.log('[TabbedAnalyticsDashboard] Rendering AI Insight Cards row with', aiInsightCards.length, 'cards');
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: aiInsightCards.length === 1 ? 'column' : 'row' },
                flexWrap: { xs: 'nowrap', md: aiInsightCards.length === 1 ? 'nowrap' : 'wrap' },
                gap: { xs: 2, md: aiInsightCards.length === 1 ? 0 : '0.5rem' },
                mt: (allKpiCards.length > 0 || performerCards.length > 0) ? 1 : 1,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                overflow: 'visible',
                boxSizing: 'border-box',
                paddingLeft: '8px',
                paddingRight: '8px',
              }}
            >
              {aiInsightCards.map((visualization, index) => {
                const isPreviewMode = (config as any)._previewMode === true;
                const showEditIcons = isPreviewMode && useMockData;
                const renderedViz = renderVisualization(visualization, tabData, false);

                return (
                  <React.Fragment key={visualization.id}>
                    {index > 0 && (
                      <Box
                        sx={{
                          display: { xs: 'none', md: 'flex' },
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '1px',
                          height: '122px',
                          flexShrink: 0,
                          backgroundColor: '#e4e4e7',
                          mx: 0,
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        flex: { xs: '1 1 100%', md: aiInsightCards.length === 1 ? '1 1 100%' : '0 0 auto' },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        height: aiInsightCards.length === 1 ? 'auto' : '122px',
                        width: { xs: '100%', md: aiInsightCards.length === 1 ? '100%' : (visualization.layout?.span ? `${(visualization.layout.span / 12) * 100}%` : '375px') },
                        minWidth: { xs: 0, md: aiInsightCards.length === 1 ? 0 : (visualization.layout?.span ? 'auto' : '375px') },
                        maxWidth: { xs: '100%', md: aiInsightCards.length === 1 ? '100%' : (visualization.layout?.span ? `${(visualization.layout.span / 12) * 100}%` : '375px') },
                      }}
                    >
                      {showEditIcons ? (
                        <VisualizationDecorator
                          visualization={visualization}
                          isPreviewMode={showEditIcons}
                          onEdit={(viz) => {
                            setSelectedVisualization(viz);
                            setPropertyEditorOpen(true);
                          }}
                        >
                          {renderedViz}
                        </VisualizationDecorator>
                      ) : (
                        renderedViz
                      )}
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          );
        })()}

        {/* Charts: Full width rows */}
        {chartVisualizations.length > 0 && (() => {
          const isPreviewMode = (config as any)._previewMode === true;
          const showEditIcons = isPreviewMode && useMockData; // Only show edit icons in design time (preview + mock data)
          return (
            <Grid container spacing={2} sx={{
              mt: 2,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}>
              {chartVisualizations.map(visualization => {
                const renderedViz = renderVisualization(visualization, tabData);
                return (
                  <Grid
                    key={visualization.id}
                    item
                    xs={visualization.layout?.span || 12}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    {showEditIcons ? (
                      <VisualizationDecorator
                        visualization={visualization}
                        isPreviewMode={showEditIcons}
                        onEdit={(viz) => {
                          setSelectedVisualization(viz);
                          setPropertyEditorOpen(true);
                        }}
                      >
                        {renderedViz}
                      </VisualizationDecorator>
                    ) : (
                      renderedViz
                    )}
                  </Grid>
                );
              })}
            </Grid>
          );
        })()}

        {/* Tables and Other visualizations */}
        {(otherVisualizations.length > 0) && (() => {
          const isPreviewMode = (config as any)._previewMode === true;
          const showEditIcons = isPreviewMode && useMockData; // Only show edit icons in design time (preview + mock data)
          return (
            <Grid container spacing={2} sx={{
              mt: chartVisualizations.length > 0 ? 0 : 2,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}>
              {otherVisualizations.map(visualization => {
                const renderedViz = renderVisualization(visualization, tabData);
                return (
                  <Grid
                    key={visualization.id}
                    item
                    xs={visualization.layout?.span || 12}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      ...((visualization.type === 'kpi' || visualization.type === 'performance-card') && {
                        minHeight: '7.625rem'
                      })
                    }}
                  >
                    {showEditIcons ? (
                      <VisualizationDecorator
                        visualization={visualization}
                        isPreviewMode={showEditIcons}
                        onEdit={(viz) => {
                          setSelectedVisualization(viz);
                          setPropertyEditorOpen(true);
                        }}
                      >
                        {renderedViz}
                      </VisualizationDecorator>
                    ) : (
                      renderedViz
                    )}
                  </Grid>
                );
              })}
            </Grid>
          );
        })()}
      </>
    );
  };

  if (!config.tabs || config.tabs.length === 0) {
    return (
      <Alert severity="warning">
        No tabs configured for this dashboard
      </Alert>
    );
  }

  const isTabLoading = loading[currentTab?.id || ''];
  const tabError = errors[currentTab?.id || ''];

  // Check if we're in preview mode (set by analytics.dashboard.resource.tsx)
  const isPreviewMode = (config as any)._previewMode === true;

  return (
    <DashboardContainer maxWidth="xl" className={className}>
      <DashboardHeader sx={isPreviewMode ? { backgroundColor: '#ffffff' } : undefined}>
        {/* Dashboard Title and Settings - conditionally rendered based on showHeader prop */}
        {showHeader && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
            {/* Global Dashboard-Level Filters */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <GlobalFilterPanel
                  filters={dynamicFilters}
                  filterValues={filterValues}
                  onFilterChange={handleFilterChange}
                  disabled={isBackendDataLoading}
                  canUndo={canUndo}
                  canRedo={canRedo}
                  onUndo={handleUndo}
                  onRedo={handleRedo}
                />
              </Box>
              {/* Recent Filters Menu */}
              <RecentFiltersMenu
                recentFilters={recentFilters}
                currentFilters={filterValues}
                onApplyFilter={handleApplyRecentFilter}
                onClearHistory={clearHistory}
                onDeleteFilters={deleteFilters}
                disabled={isBackendDataLoading}
              />
            </Box>

            {/* Settings Popover */}
            <Box>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Chip
                  icon={isBackendDataLoading ? <CircularProgress size={14} sx={{ color: '#f97316' }} /> : (useMockData ? <MockIcon /> : <CloudIcon />)}
                  label={isBackendDataLoading ? 'Loading...' : (useMockData ? 'Mock Data' : 'Live Data')}
                  variant="outlined"
                  size="small"
                  color={isBackendDataLoading ? 'warning' : (useMockData ? 'warning' : 'success')}
                  clickable={!isBackendDataLoading}
                  onClick={() => !isBackendDataLoading && setUseMockData(!useMockData)}
                  sx={{
                    mr: 1,
                    cursor: isBackendDataLoading ? 'default' : 'pointer',
                    opacity: isBackendDataLoading ? 0.8 : 1,
                    '&:hover': !isBackendDataLoading ? {
                      backgroundColor: useMockData ? 'warning.light' : 'success.light',
                      borderColor: useMockData ? 'warning.main' : 'success.main',
                    } : {},
                    '&:active': !isBackendDataLoading ? {
                      transform: 'scale(0.95)',
                    } : {},
                    transition: 'all 0.2s ease-in-out',
                  }}
                />
                {isBackendDataLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      mt: 1,
                      px: 2,
                      py: 1,
                      backgroundColor: 'warning.main',
                      color: 'white',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                      zIndex: 1500,
                      boxShadow: 3,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -4,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '4px solid transparent',
                        borderRight: '4px solid transparent',
                        borderBottom: '4px solid',
                        borderBottomColor: 'warning.main',
                      }
                    }}
                  >
                    Loading...
                  </Box>
                )}
              </Box>
              <IconButton
                onClick={handleSettingsClick}
                size="small"
                sx={isPreviewMode ? { color: '#18181b' } : undefined}
              >
                <SettingsIcon />
              </IconButton>
              <Popover
                open={settingsOpen}
                anchorEl={settingsAnchor}
                onClose={handleSettingsClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <Box sx={{ p: 2, minWidth: 200 }}>
                  <Typography variant="h6" gutterBottom>
                    Dashboard Settings
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Data Export
                  </Typography>

                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Download dashboard data. Choose tab data (filtered), comprehensive dataset template (with integration guide), or raw data array only.
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    {/* Export Mode Selection */}
                    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                      <InputLabel sx={{ color: 'grey.400' }}>Export Mode</InputLabel>
                      <Select
                        value={exportMode}
                        onChange={(e) => {
                          setExportMode(e.target.value as 'tab' | 'dataset' | 'data-only');
                          setSelectedDataset(''); // Reset dataset selection
                        }}
                        label="Export Mode"
                        sx={{
                          color: 'white',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'grey.600',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'grey.400',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'grey.400',
                          },
                        }}
                      >
                        <MenuItem value="tab">Tab Data (Current Tab, Filtered)</MenuItem>
                        <MenuItem value="dataset">Dataset (Comprehensive, Server Template)</MenuItem>
                        <MenuItem value="data-only">Data Only (Raw Data Array, No Metadata)</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Dataset Selection (shown for dataset and data-only modes) */}
                    {(exportMode === 'dataset' || exportMode === 'data-only') && (
                      <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                        <InputLabel sx={{ color: 'grey.400' }}>Select Dataset</InputLabel>
                        <Select
                          value={selectedDataset}
                          onChange={(e) => setSelectedDataset(e.target.value)}
                          label="Select Dataset"
                          sx={{
                            color: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'grey.600',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'grey.400',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'grey.400',
                            },
                          }}
                        >
                          {getAvailableDatasets().map(dataset => (
                            <MenuItem key={dataset.id} value={dataset.id}>
                              {dataset.name} ({dataset.id})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                      <InputLabel sx={{ color: 'grey.400' }}>Export Format</InputLabel>
                      <Select
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value as 'csv' | 'json' | 'xlsx')}
                        label="Export Format"
                        sx={{
                          color: 'white',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'grey.600',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'grey.400',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'grey.400',
                          },
                        }}
                      >
                        <MenuItem value="csv">CSV - Comma Separated Values</MenuItem>
                        <MenuItem value="json">JSON - JavaScript Object Notation</MenuItem>
                        <MenuItem value="xlsx">XLSX - Excel Spreadsheet (converted to CSV)</MenuItem>
                      </Select>
                    </FormControl>

                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={handleDataExport}
                      disabled={
                        isExporting ||
                        (exportMode === 'tab' && (!activeTab || !tabData[activeTab?.id || ''])) ||
                        ((exportMode === 'dataset' || exportMode === 'data-only') && !selectedDataset)
                      }
                      fullWidth
                      size="small"
                    >
                      {isExporting
                        ? 'Exporting...'
                        : exportMode === 'dataset'
                          ? `Download Dataset ${exportFormat.toUpperCase()}`
                          : exportMode === 'data-only'
                            ? `Download Data ${exportFormat.toUpperCase()}`
                            : `Download Tab ${exportFormat.toUpperCase()}`
                      }
                    </Button>
                  </Box>

                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                    {exportMode === 'dataset'
                      ? `📊 Dataset: ${selectedDataset || 'None selected'} • Comprehensive template with metadata & instructions`
                      : exportMode === 'data-only'
                        ? `📋 Dataset: ${selectedDataset || 'None selected'} • Raw data array only, no metadata`
                        : `Data source: ${useMockData ? '📊 Mock data (generated)' : '🌐 Live data (server)'} • Active tab: ${activeTab?.name || 'None'}`
                    }
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Configuration Validation
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {hasErrors && (
                      <Chip
                        size="small"
                        color="error"
                        label={`${errorCount} Error${errorCount !== 1 ? 's' : ''}`}
                      />
                    )}
                    {hasWarnings && (
                      <Chip
                        size="small"
                        color="warning"
                        label={`${warningCount} Warning${warningCount !== 1 ? 's' : ''}`}
                      />
                    )}
                    {!hasErrors && !hasWarnings && validationResult?.isValid && (
                      <Chip
                        size="small"
                        color="success"
                        label="Valid"
                      />
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={validate}
                      disabled={isValidating}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {isValidating ? 'Validating...' : 'Validate'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setShowValidation(!showValidation)}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {showValidation ? 'Hide' : 'Show'} Details
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => setShowFullValidationPanel(true)}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Advanced
                    </Button>
                  </Box>

                  {showValidation && validationResult && (
                    <Box sx={{
                      mt: 2,
                      maxHeight: 200,
                      overflow: 'auto',
                      p: 2,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      border: 1,
                      borderColor: '#d0d0d0'
                    }}>
                      <Typography variant="caption" sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-line',
                        fontSize: '0.75rem',
                        color: '#333333',
                        lineHeight: 1.4
                      }}>
                        {formatResults()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Popover>
            </Box>
          </Box>
        )}

        {/* Global Dashboard-Level Filters - shown even when header is hidden (runtime mode) */}
        {!showHeader && (dynamicFilters && dynamicFilters.length > 0) && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <GlobalFilterPanel
                filters={dynamicFilters}
                filterValues={filterValues}
                onFilterChange={handleFilterChange}
                disabled={isBackendDataLoading}
                onOpenDialogRef={onOpenFilterDialog}
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={handleUndo}
                onRedo={handleRedo}
              />
            </Box>
            {/* Recent Filters Menu */}
            <RecentFiltersMenu
              recentFilters={recentFilters}
              currentFilters={filterValues}
              onApplyFilter={handleApplyRecentFilter}
              onClearHistory={clearHistory}
              onDeleteFilters={deleteFilters}
              disabled={isBackendDataLoading}
            />
          </Box>
        )}

        {/* Main Tab Navigation - hide if only one tab and header is hidden */}
        {(showHeader || config.tabs.length > 1) && (
          <Box
            sx={{
              borderBottom: '1px solid #e4e4e7',
              paddingLeft: '12px',
              paddingRight: '12px',
              paddingTop: 0,
              paddingBottom: 0,
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              backgroundColor: 'transparent',
            }}
          >
            <Tabs
              value={activeTabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                backgroundColor: 'transparent !important',
                minHeight: 'auto !important',
                height: 'auto !important',
                flex: 1,
                width: '100%',
                '&.MuiTabs-root': {
                  backgroundColor: 'transparent !important',
                  minHeight: 'auto !important',
                  width: '100%',
                },
                '& .MuiTabs-indicator': {
                  display: 'none !important',
                  height: '0 !important',
                },
                '& .MuiTabs-flexContainer': {
                  gap: '0 !important',
                  borderBottom: 'none !important',
                  display: 'flex !important',
                  alignItems: 'center !important',
                  padding: '0 !important',
                  margin: '0 !important',
                  '& > *': {
                    marginTop: '0 !important',
                    marginBottom: '0 !important',
                    padding: '0 !important',
                    paddingLeft: '0 !important',
                    paddingTop: '0 !important',
                    paddingBottom: '0 !important',
                    paddingRight: '0 !important',
                  },
                  '& > *:first-child': {
                    marginLeft: '0 !important',
                    marginRight: '12px !important',
                  },
                  '& > *:not(:first-child):not(:last-child)': {
                    marginLeft: '12px !important',
                    marginRight: '12px !important',
                  },
                  '& > *:last-child': {
                    marginLeft: '12px !important',
                    marginRight: '0 !important',
                  },
                },
                '& .MuiTabs-scroller': {
                  padding: '0 !important',
                  margin: '0 !important',
                  gap: '0 !important',
                },
                '& .MuiTabs-scrollButtons': {
                  display: 'none !important',
                  padding: '0 !important',
                  margin: '0 !important',
                },
                '& .MuiTab-root': {
                  color: '#71717a !important',
                  fontFamily: "'Inter', sans-serif !important",
                  fontWeight: '400 !important',
                  fontSize: '14px !important',
                  lineHeight: '20px !important',
                  textTransform: 'none !important',
                  minHeight: 'auto !important',
                  minWidth: 'auto !important',
                  height: 'auto !important',
                  width: 'auto !important',
                  padding: '8px 0 !important',
                  paddingTop: '8px !important',
                  paddingBottom: '8px !important',
                  paddingLeft: '0 !important',
                  paddingRight: '0 !important',
                  margin: '0 !important',
                  marginTop: '0 !important',
                  marginBottom: '0 !important',
                  marginLeft: '0 !important',
                  marginRight: '0 !important',
                  flexShrink: 0,
                  backgroundColor: 'transparent !important',
                  borderBottom: '2px solid transparent !important',
                  borderRadius: '0 !important',
                  boxShadow: 'none !important',
                  textIndent: '0 !important',
                  letterSpacing: 'normal !important',
                  '& .MuiTab-wrapper': {
                    padding: '0 12px !important',
                    paddingTop: '0 !important',
                    paddingBottom: '0 !important',
                    paddingLeft: '12px !important',
                    paddingRight: '12px !important',
                    margin: '0 !important',
                    marginTop: '0 !important',
                    marginBottom: '0 !important',
                    marginLeft: '0 !important',
                    marginRight: '0 !important',
                    borderRadius: '6px !important',
                    display: 'flex !important',
                    alignItems: 'center !important',
                    justifyContent: 'center !important',
                    gap: '0 !important',
                    whiteSpace: 'nowrap !important',
                    width: 'auto !important',
                    height: 'auto !important',
                    textIndent: '0 !important',
                    letterSpacing: 'normal !important',
                  },
                  '& .MuiTab-textColorInherit': {
                    padding: '0 !important',
                    margin: '0 !important',
                  },
                  '&:hover:not(.Mui-disabled)': {
                    backgroundColor: 'transparent !important',
                    color: '#18181b !important',
                    '& .MuiTab-wrapper': {
                      backgroundColor: 'transparent !important',
                    },
                  },
                  '&.Mui-selected': {
                    color: '#18181b !important',
                    fontWeight: '400 !important',
                    backgroundColor: 'transparent !important',
                    borderBottom: '2px solid #18181b !important',
                    boxShadow: 'none !important',
                    '& .MuiTab-wrapper': {
                      backgroundColor: 'transparent !important',
                    },
                  },
                },
              }}
            >
              {config.tabs.map((tab) => (
                <Tab
                  key={tab.id}
                  label={tab.name}
                  sx={{
                    padding: '0 !important',
                    margin: '0 !important',
                    minWidth: 'auto !important',
                    textIndent: '0 !important',
                    letterSpacing: 'normal !important',
                    '& .MuiTab-wrapper': {
                      padding: '0 !important',
                      margin: '0 !important',
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Sub-tab Navigation - only for tabs without shared visualizations */}
        {activeTab?.subTabs && activeTab.subTabs.length > 0 && !activeTab.visualizations && (
          <Tabs
            value={activeSubTabIndex}
            onChange={handleSubTabChange}
            variant="fullWidth"
            sx={{
              mt: 2,
              backgroundColor: isPreviewMode ? '#f4f4f5' : 'grey.900',
              borderRadius: '6px',
              border: '1px solid',
              borderColor: isPreviewMode ? '#e4e4e7' : 'grey.700',
              '& .MuiTabs-indicator': {
                backgroundColor: 'secondary.main',
                height: 2,
              },
              '& .MuiTab-root': {
                color: isPreviewMode ? '#71717a' : 'grey.500',
                fontWeight: 400,
                fontSize: '0.8rem',
                textTransform: 'capitalize',
                minHeight: 36,
                transition: 'all 0.2s ease-in-out',
                borderRight: 'none',
                '&:hover': {
                  backgroundColor: isPreviewMode ? '#e4e4e7' : 'grey.800',
                  color: isPreviewMode ? '#18181b' : 'grey.300',
                },
                '&.Mui-selected': {
                  color: 'secondary.main',
                  backgroundColor: isPreviewMode ? '#ffffff' : 'grey.800',
                  fontWeight: 500,
                },
              },
            }}
          >
            {activeTab.subTabs.map((subTab) => (
              <Tab key={subTab.id} label={subTab.name} />
            ))}
          </Tabs>
        )}
      </DashboardHeader>

      <ContentArea>
        {config.tabs.map((tab, index) => {
          // Tab with both shared visualizations and subTabs
          if (tab.visualizations && tab.subTabs && tab.subTabs.length > 0) {
            return (
              <TabPanelComponent key={tab.id} value={activeTabIndex} index={index}>
                {tabError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {tabError}
                  </Alert>
                )}

                {/* Shared Visualizations (Chart and KPIs) */}
                {renderVisualizationsGrid(tab.visualizations, tabData[tab.id])}

                {/* Sub-tab Navigation */}
                <Box sx={{ mb: 3, mt: 3 }}>
                  <Tabs
                    value={activeSubTabIndex}
                    onChange={handleSubTabChange}
                    variant="fullWidth"
                    sx={{
                      backgroundColor: isPreviewMode ? '#f4f4f5' : 'grey.900',
                      borderRadius: '6px',
                      border: '1px solid',
                      borderColor: isPreviewMode ? '#e4e4e7' : 'grey.700',
                      '& .MuiTabs-indicator': {
                        backgroundColor: 'secondary.main',
                        height: 2,
                      },
                      '& .MuiTab-root': {
                        color: isPreviewMode ? '#71717a' : 'grey.500',
                        fontWeight: 400,
                        fontSize: '0.8rem',
                        textTransform: 'capitalize',
                        minHeight: 36,
                        transition: 'all 0.2s ease-in-out',
                        borderRight: 'none',
                        '&:hover': {
                          backgroundColor: isPreviewMode ? '#e4e4e7' : 'grey.800',
                          color: isPreviewMode ? '#18181b' : 'grey.300',
                        },
                        '&.Mui-selected': {
                          color: 'secondary.main',
                          backgroundColor: isPreviewMode ? '#ffffff' : 'grey.800',
                          fontWeight: 500,
                        },
                      },
                    }}
                  >
                    {tab.subTabs.map((subTab) => (
                      <Tab key={subTab.id} label={subTab.name} />
                    ))}
                  </Tabs>
                </Box>

                {/* Sub-tab Content */}
                {tab.subTabs.map((subTab, subIndex) => (
                  <TabPanelComponent key={subTab.id} value={activeSubTabIndex} index={subIndex}>
                    <VisualizationGrid container spacing={3}>
                      {subTab.visualizations?.filter(passesSingleLocationLayout).map((visualization) => {
                        const gridSize = visualization.layout?.span || 12;

                        return (
                          <Grid item xs={gridSize} key={visualization.id}>
                            {renderVisualization(visualization, tabData[tab.id])}
                          </Grid>
                        );
                      })}
                    </VisualizationGrid>
                  </TabPanelComponent>
                ))}
              </TabPanelComponent>
            );
          }

          // Tab with only subTabs (no shared visualizations)
          if (tab.subTabs && tab.subTabs.length > 0) {
            return (
              <TabPanelComponent key={tab.id} value={activeTabIndex} index={index}>
                {tab.subTabs.map((subTab, subIndex) => (
                  <TabPanelComponent key={subTab.id} value={activeSubTabIndex} index={subIndex}>
                    {tabError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {tabError}
                      </Alert>
                    )}

                    <Box sx={{ p: 0 }}>
                      {activeSubTab.visualizations && (
                        renderVisualizationsGrid(activeSubTab.visualizations, tabData[tab.id])
                      )}
                    </Box>
                  </TabPanelComponent>
                ))}
              </TabPanelComponent>
            );
          }

          // Regular tab without subTabs
          return (
            <TabPanelComponent key={tab.id} value={activeTabIndex} index={index}>
              {tabError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {tabError}
                </Alert>
              )}

              {renderVisualizationsGrid(tab.visualizations, tabData[tab.id])}
            </TabPanelComponent>
          );
        })}
      </ContentArea>

      {/* Full Validation Panel Modal */}
      {showFullValidationPanel && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
          onClick={() => setShowFullValidationPanel(false)}
        >
          <Box
            sx={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              width: '800px',
              overflow: 'hidden',
              backgroundColor: 'white',
              borderRadius: 2
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <ConfigValidationPanel
              validationResult={validationResult}
              isValidating={isValidating}
              onValidate={validate}
              onCopyResults={handleCopyValidationResults}
            />
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', textAlign: 'right' }}>
              <Button
                variant="contained"
                onClick={() => setShowFullValidationPanel(false)}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Subtle Loading Overlay - Blocks content interaction but keeps header visible */}
      {isBackendDataLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: '120px', // Start below the header area
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: theme => theme.zIndex.modal - 1, // Lower than header but above content
            pointerEvents: 'auto', // Block interactions
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            pt: 8,
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(40, 40, 40, 0.9) 100%)',
              backdropFilter: 'blur(30px) saturate(150%)',
              border: '2px solid rgba(249, 115, 22, 0.4)',
              borderRadius: 6,
              p: 5,
              boxShadow: `
                0 25px 50px rgba(0, 0, 0, 0.8),
                0 0 0 1px rgba(249, 115, 22, 0.3) inset,
                0 2px 0 rgba(249, 115, 22, 0.2) inset,
                0 0 20px rgba(249, 115, 22, 0.1)
              `,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              maxWidth: 550,
              position: 'relative',
              overflow: 'hidden',
              transform: 'translateZ(0)', // Hardware acceleration
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, transparent, rgba(249, 115, 22, 0.6), transparent)',
                borderRadius: '6px 6px 0 0',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '200%',
                height: '100%',
                background: 'linear-gradient(45deg, transparent 30%, rgba(249, 115, 22, 0.1) 50%, transparent 70%)',
                animation: 'shimmer 5s ease-in-out infinite',
              },
              '@keyframes shimmer': {
                '0%': { transform: 'translateX(-100%)' },
                '50%': { transform: 'translateX(100%)' },
                '100%': { transform: 'translateX(-100%)' },
              },
            }}
          >
            <CircularProgress
              size={36}
              thickness={5}
              sx={{
                color: '#f97316',
                filter: 'drop-shadow(0 3px 6px rgba(249, 115, 22, 0.3))',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'rgba(220, 220, 220, 0.95)',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)',
                position: 'relative',
                zIndex: 1,
                letterSpacing: '0.3px',
              }}
            >
              Constructing Your Advanced Analytics DataSet, This Will Take A Few Moments
            </Typography>
          </Box>
        </Box>
      )}

      {/* Conflict Resolution Dialog (AC2) */}
      <Dialog
        open={showConflictDialog}
        onClose={() => {/* Don't allow closing without choosing */ }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Filter Conflict Detected</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Another tab has updated the filters while you were editing.
            Choose which version to keep:
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50'
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                  Your Changes (This Tab)
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto', mt: 2 }}>
                  {conflictData && Object.entries(conflictData.local).map(([key, value]) => (
                    <Box key={key} sx={{ mb: 1, pb: 1, borderBottom: '1px solid #eee' }}>
                      <Typography variant="caption" color="text.secondary">
                        {key}
                      </Typography>
                      <Typography variant="body2">
                        {Array.isArray(value)
                          ? `${value.length} selected: ${value.slice(0, 3).join(', ')}${value.length > 3 ? '...' : ''}`
                          : String(value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  border: '2px solid',
                  borderColor: 'info.main',
                  bgcolor: 'info.50'
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="info.main">
                  Other Tab's Changes
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto', mt: 2 }}>
                  {conflictData && Object.entries(conflictData.remote).map(([key, value]) => (
                    <Box key={key} sx={{ mb: 1, pb: 1, borderBottom: '1px solid #eee' }}>
                      <Typography variant="caption" color="text.secondary">
                        {key}
                      </Typography>
                      <Typography variant="body2">
                        {Array.isArray(value)
                          ? `${value.length} selected: ${value.slice(0, 3).join(', ')}${value.length > 3 ? '...' : ''}`
                          : String(value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Other tab version: {conflictData?.remoteVersion} •
              Updated: {conflictData ? new Date(conflictData.remoteTimestamp).toLocaleTimeString() : ''}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              // APPLY REMOTE - use remote for persisted filters, keep local for session-only
              if (conflictData) {
                const deserialized = deserializeFilterValue(conflictData.remote);
                setFilterValues(prev => {
                  const merged = { ...prev, ...deserialized };
                  Object.keys(prev).filter(k => isSessionOnlyFilter(k, config.filters)).forEach(k => {
                    merged[k] = prev[k];
                  });
                  return merged;
                });
                setLocalVersion(conflictData.remoteVersion);
                setLocalTimestamp(conflictData.remoteTimestamp);
                setHasLocalChanges(false);
                setShowConflictDialog(false);
                setSnackbarMessage('Applied changes from other tab');
                setSnackbarSeverity('info');
                setSnackbarOpen(true);
              }
            }}
            variant="outlined"
            color="info"
          >
            Use Other Tab's Changes
          </Button>

          <Button
            onClick={() => {
              // KEEP MINE - re-save local changes with new version (exclude session-only)
              if (conflictData) {
                const newVersion = conflictData.remoteVersion + 1;
                const newTimestamp = Date.now();
                const toPersist = filterValuesForPersistence(filterValues, config.filters);
                const filterState: FilterState = {
                  values: serializeFilterValue(toPersist),
                  version: newVersion,
                  timestamp: newTimestamp,
                  tabId: tabId.current
                };
                localStorage.setItem(
                  `dashboard:filters:${dashboardKey}`,
                  JSON.stringify(filterState)
                );
                setLocalVersion(newVersion);
                setLocalTimestamp(newTimestamp);
                setHasLocalChanges(false);
                setShowConflictDialog(false);
                setSnackbarMessage('Your changes kept and synced to other tabs');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
              }
            }}
            variant="contained"
            color="primary"
          >
            Keep My Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar notification for filter resets and multi-tab updates */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarSeverity === 'info' ? 3000 : 6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          icon={snackbarSeverity === 'info' ? <InfoIcon /> : undefined}
          sx={{
            width: '100%',
            maxWidth: '500px',
            '& .MuiAlert-icon': {
              fontSize: '24px'
            }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Property Editor Dialog */}
      <PropertyEditor
        open={propertyEditorOpen}
        visualization={selectedVisualization}
        onClose={() => {
          setPropertyEditorOpen(false);
          setSelectedVisualization(null);
        }}
        onSave={handlePropertySave}
      />

    </DashboardContainer>
  );
}; 